/**
 * gbusinessService.js — wrappers over the Django `gbusiness` API.
 *
 * All requests go through the shared `httpClient` (session cookie + CSRF).
 * The OAuth connect flow is a full-page redirect: we don't fetch the connect
 * URL via XHR because the Google consent screen requires a top-level redirect.
 */

import httpClient from './httpClient';

const BASE = '/gbusiness';

/**
 * Aggregated multi-account status + billing snapshot:
 *
 *   {
 *     connected, count, primary,
 *     plan, isPro, subscriptionStatus, trialEnd,
 *     purchasedSeats, maxSeats, availableSeats,
 *     activeLocationIds, seatedLocationIds, defaultLocationIds,
 *   }
 *
 * Billing fields come from `MeView` (extended in billing-2) and let pages
 * decide between "connect" and "subscribe" overlay modes without an extra
 * `/billing/me` round-trip.
 *
 * Legacy fields `name`, `email`, `picture` are lifted from `primary` for
 * callers that haven't migrated to the multi-account shape yet.
 */
export async function getGbusinessMe() {
  const { data } = await httpClient.get(`${BASE}/auth/me`);
  return {
    connected: !!data.connected,
    count: data.count || 0,
    // ≥1 connection's Google token expired/was revoked. With connected=false
    // it tells the gates to show "reconnect" instead of first-run "connect".
    reauthRequired: !!data.reauth_required,
    primary: data.primary || null,
    // Legacy compat (single-account callers).
    name: data.primary?.name || null,
    email: data.primary?.email || null,
    picture: data.primary?.picture || null,
    // Billing fields (added in billing-2).
    plan: data.plan || 'free',
    isPro: !!data.is_pro,
    // Cuenta demo (decisiones §139). Es el ÚNICO sitio de la interfaz donde la
    // marca asoma, y solo para que el Escáner SEO pinte su engranaje: el resto
    // tiene que verse como la cuenta de cualquier cliente.
    isDemo: !!data.is_demo,
    // Paying Pro = Pro that isn't a Stripe trial. Gates the review-backfill
    // section on Automations: trialing users are isPro but not isPayingPro.
    isPayingPro: !!data.is_paying_pro,
    // Hay una suscripcion de Stripe que se puede modificar. NO es "es Pro":
    // un grandfathered, un enterprise y un comp lo son y no la tienen, asi
    // que ofrecerles ampliar el plan acaba en un 502. Lo decide el backend
    // porque la SPA no recibe los campos con los que decidirlo.
    stripeManaged: !!data.stripe_managed,
    subscriptionStatus: data.subscription_status || null,
    trialEnd: data.trial_end || null,
    purchasedSeats: data.purchased_seats || 0,
    maxSeats: data.max_seats || 0,
    availableSeats: data.available_seats || 0,
    activeLocationIds: Array.isArray(data.active_location_ids)
      ? data.active_location_ids : [],
    // ¿Este actor está acotado a un subconjunto de locales? Viaja aquí y no
    // solo en `/auth/me` porque las listas de arriba YA vienen recortadas, y
    // sin la marca una lista vacía es indistinguible de "no ha conectado
    // Google" — que es un muro que le ofrece acciones del titular.
    locationScoped: !!data.location_scoped,
    // Los locales que llevan ASIENTO. Ojo: no es lo mismo que «llevan
    // Business». La lista le sobrevive a la baja —el recorte por cupo es una
    // lectura, para que un cliente que vuelve recupere su reparto— y su suelo
    // es 1, así que un free tiene siempre uno. Eso es el CARRIL: donde caen sus
    // asignaciones por local, las gratuitas incluidas. Para pintar el plan,
    // `businessLocationIds` de `lib/gbusinessLocationStore`.
    seatedLocationIds: Array.isArray(data.seated_location_ids)
      ? data.seated_location_ids : [],
    // Los que llevan Business, resueltos por el backend. `undefined` (y no `[]`)
    // si el backend no lo manda, para que `businessLocationIds` caiga a su
    // deducción de siempre durante el despliegue.
    businessLocationIds: Array.isArray(data.business_location_ids)
      ? data.business_location_ids : undefined,
    // Sobre qué locales se trabaja cuando no se pide ninguno: los que llevan
    // Business si hay alguno, si no todos los activos. Lo resuelve el backend
    // para que la regla no viva en dos sitios. Viene SIN podar por conexión
    // viva ni por verificación —de eso no se entera el backend en este
    // endpoint—, así que quien lo use contra una lista ya cargada de locales
    // tiene que intersecar, como hace `pickDefaultLocationId`.
    // El fallback cubre la ventana de despliegue: una SPA nueva contra un
    // backend que todavía no manda el campo se quedaría con la lista vacía y sin
    // un solo local que enseñar. Vacío TENIENDO activos no es un estado posible
    // —el backend nunca vacía esta lista—, así que no tapa ningún caso real.
    defaultLocationIds: (Array.isArray(data.default_location_ids)
      && data.default_location_ids.length)
      ? data.default_location_ids
      : (Array.isArray(data.active_location_ids) ? data.active_location_ids : []),
    priceFamily: data.price_family || null,
    // Si el cliente todavia no ha dicho en que local quiere su plan. Solo se
    // pone al contratar sin elegir local; lo cierra el servidor en cuanto elige.
    seatsPendingChoice: !!data.seats_pending_choice,
    trialUsed: !!data.trial_used,
    // Lifecycle surface (billing-3.3 / §26.17). Lets GbusinessSettings
    // (and any other page reading /gbusiness/auth/me) decide whether to
    // show the cancellation-pending / past-due banners without a second
    // hop to /billing/me.
    cancelAtPeriodEnd: !!data.cancel_at_period_end,
    currentPeriodEnd: data.current_period_end || null,
    proSince: data.pro_since || null,
    canceledAt: data.canceled_at || null,
    paymentFailedAt: data.payment_failed_at || null,
    // Impago: hasta cuándo conserva el acceso mientras Stripe reintenta, y por
    // qué rebotó el cobro. Los consume `BillingStatusBanner`, que va montado en
    // todas las páginas y necesita poder decir algo concreto.
    pastDueGraceUntil: data.past_due_grace_until || null,
    lastPaymentError: data.last_payment_error || null,
    billingPeriod: data.billing_period || null,
    // Admin-assigned Pro (comp plan, no Stripe). SubscriptionCard gates the
    // Stripe-y UI (portal link, invoices card, change-plan / cancel actions)
    // when true. `_until` null = indefinite.
    adminAssignedPro: !!data.admin_assigned_pro,
    adminAssignedProUntil: data.admin_assigned_pro_until || null,
    // Comp en MODO CLOSING: Business como en una prueba. Ajustes le pinta la
    // card de contratar como a un free y el backfill sale bloqueado (el backend
    // ya lo deja fuera de `isPayingPro`).
    closingMode: !!data.closing_mode,
    // First-run UI flags (§34). Used by Reviews to gate the welcome wizard.
    uiFlags: data.ui_flags && typeof data.ui_flags === 'object' ? data.ui_flags : {},
    // Estado del recorrido guiado. Viaja aquí —y no en una petición aparte—
    // porque el SPA ya pide `/auth/me` en cada carga: así el stepper se pinta
    // sin coste y las páginas sin card no pagan nada. Los DATOS de cada paso sí
    // van aparte, en `getGbusinessOnboarding(step)`.
    onboarding: data.onboarding && typeof data.onboarding === 'object'
      ? data.onboarding
      : null,
    // Vídeo en publicaciones (§178), conmutado por entorno en el backend.
    videoPostsEnabled: !!data.video_posts_enabled,
    // Plano Google Maps pt (`QR/billing/oferta_pt.py`). Lo consume `useOfertaPt`;
    // `null` si el backend no la manda (flag apagado en un backend viejo).
    ofertaPt: mapOfertaPt(data.oferta_pt),
  };
}

/**
 * La forma camelCase del estado de la oferta pt. Exportada porque la landing
 * devuelve el mismo objeto.
 */
export function mapOfertaPt(o) {
  if (!o || typeof o !== 'object') return null;
  return {
    elegible: !!o.elegible,
    abierta: !!o.abierta,
    activa: !!o.activa,
    pendientePopup: !!o.pendiente_popup,
    cierreAt: o.cierre_at || null,
    plazasRestantes: Number.isFinite(o.plazas_restantes) ? o.plazas_restantes : null,
    plazasTotal: Number.isFinite(o.plazas_total) ? o.plazas_total : 50,
    origen: o.origen || null,
    usada: !!o.usada,
  };
}

/**
 * POST /gbusiness/auth/ui-flags — persist a UI flag on the user document.
 * Backend whitelists the allowed keys, so unknown keys return 400.
 */
export async function setGbusinessUiFlag(key, value) {
  const { data } = await httpClient.post(`${BASE}/auth/ui-flags`, { key, value });
  return data;
}

/**
 * GET /gbusiness/onboarding — estado del recorrido guiado y, con `step`, los
 * datos REALES de ese paso.
 *
 * Sin `step` cuesta un documento que el backend ya tenía cargado; con `step`
 * calcula solo ese. Se pide de uno en uno a propósito: la card está anclada a
 * una página, así que en pantalla nunca hay más de un paso, y calcular los
 * cinco costaría un Health Score completo y un escaneo de 40 KB por render.
 *
 * En cada paso, `data: null` significa «no hay datos suficientes» y el cliente
 * pinta la variante sin cifras. Es distinto de `computed: false`, que significa
 * «ni se ha intentado».
 */
export async function getGbusinessOnboarding(step = null) {
  const { data } = await httpClient.get(`${BASE}/onboarding`, {
    params: step ? { step } : undefined,
  });
  return {
    eligible: !!data.eligible,
    status: data.status || 'not_eligible',
    step: data.step ?? null,
    totalSteps: data.total_steps ?? 5,
    startedAt: data.started_at || null,
    completedAt: data.completed_at || null,
    // Abandonado con la X: cierre definitivo, y por eso el estado también pasa
    // a `dismissed`. El cliente lo usa para reconciliar un cierre cuyo POST se
    // perdió (ver `useOnboardingHeal`).
    dismissedAt: data.dismissed_at || null,
    skippedAt: data.skipped_at || null,
    steps: (data.steps || []).map((s) => ({
      key: s.key,
      page: s.page,
      order: s.order,
      computed: !!s.computed,
      data: s.data ?? null,
      // Solo lo trae el paso del Escáner SEO: su escaneo de cortesía puede
      // seguir en vuelo, y esto es lo que le dice al cliente si repreguntar.
      pending: !!s.pending,
    })),
  };
}

/**
 * POST /gbusiness/onboarding — mueve el recorrido.
 *
 * Las cinco acciones —`start`, `advance`, `complete`, `snooze`, `dismiss`— son
 * idempotentes en el servidor, y el avance es MONÓTONO: mandar un paso menor
 * que el guardado no retrocede nada. Eso importa porque la card está anclada a
 * la página, así que volver a /Reviews desde el paso 4 dispara un `advance` al
 * 1.
 *
 * `step` significa dos cosas distintas según la acción, y no es un descuido: en
 * `advance` es a dónde va el usuario, y en `dismiss` es dónde estaba al cerrar
 * —el paso que no le convenció—, que es lo que el servidor no puede deducir de
 * la posición guardada.
 */
export async function setGbusinessOnboarding(action, step = undefined) {
  const { data } = await httpClient.post(`${BASE}/onboarding`, { action, step });
  return data;
}

/**
 * GET /auth/connections — every connected Google account for the current user.
 *
 * Returns Array<{email, name, picture, status, connectedAt}> where `status`
 * is one of:
 *   'ok'                 — fully usable connection.
 *   'no_business'        — the Google account has no GBP associated.
 *   'insufficient_scope' — user did not grant the business.manage scope.
 *
 * Broken connections (the last two) are still listed so the UI can show a
 * persistent banner asking the user to reconnect with proper consent.
 */
export async function listGbusinessConnections() {
  const { data } = await httpClient.get(`${BASE}/auth/connections`);
  return (Array.isArray(data) ? data : []).map((c) => ({
    email: c.email,
    name: c.name || '',
    picture: c.picture || null,
    status: c.status || 'ok',
    connectedAt: c.connected_at || null,
  }));
}

/**
 * DELETE /auth/connections/<email> — revoke + delete one connection.
 * Other connections are untouched. Returns { disconnected: bool }.
 */
export async function disconnectGbusinessConnection(email) {
  const { data } = await httpClient.delete(
    `${BASE}/auth/connections/${encodeURIComponent(email)}`,
  );
  return data;
}

/**
 * Legacy "disconnect all". Revokes every connection at once. New code
 * should prefer `disconnectGbusinessConnection(email)` so users can keep
 * the rest of their connections.
 */
export async function disconnectGbusiness() {
  const { data } = await httpClient.post(`${BASE}/auth/disconnect`);
  return data;
}

/**
 * Absolute URL for the OAuth connect endpoint. The backend will 302 to Google;
 * Google then redirects back to /api/v1/gbusiness/auth/callback, which finally
 * bounces to GBUSINESS_FRONTEND_REDIRECT_AFTER_CONNECT (e.g. /GbusinessConnect).
 *
 * Use `window.location.href = getGbusinessConnectUrl()` (NOT axios), so the
 * browser does a real navigation and Google's consent page renders top-level.
 *
 * Los botones de «conectar» NO la llaman directamente: llaman a
 * `abrirGuiaGoogle(ctaId)` (`lib/googleConnectGuide.js`), que enseña antes la
 * casilla que hay que marcar, y es el botón de ese modal el que navega aquí.
 *
 * El `ctaId` es lo que cierra el embudo. El click se mide en el navegador, pero
 * quien sabe si Google devolvió una ficha es el CALLBACK, ya en el servidor y
 * después de un round-trip completo por accounts.google.com. Mandando el id en
 * la query, `ConnectView` lo guarda en la sesión —al lado del `code_verifier`
 * del PKCE, que resuelve este mismo problema en este mismo flujo— y el callback
 * lo recupera para emitir el evento `connect`. Sin esto solo se sabría cuánta
 * gente pulsa y cuánta acaba conectada, nunca qué botón consiguió qué conexión.
 *
 * Se valida contra la allowlist en el servidor: aquí es un parámetro de URL y
 * como tal no se cree nada de lo que traiga.
 *
 * Con el id viaja también la PANTALLA desde la que se pulsó (`?from=`), y se
 * lee aquí en vez de recibirla por parámetro justo porque nadie se acordaría de
 * pasarla: `NoActiveLocationBanner` lo montan catorce páginas y el `ctaId` es el
 * mismo en todas, así que sin la ruta el panel sabe qué botón trajo la conexión
 * pero no desde dónde. El servidor la sanea igual que la del click (§125).
 *
 * `hintEmail` es la cuenta que se está REconectando. El servidor la pasa a
 * Google como `login_hint` —solo si es una conexión del propio usuario— para
 * que abra con esa cuenta elegida en vez del selector.
 *
 * `otraCuenta` es lo contrario: obliga a Google a enseñar el selector de
 * cuentas (`?otra=1` → `prompt=select_account`). Lo usa «probar con otra
 * cuenta» tras volver sin ficha; sin él, con una sola sesión de Google abierta,
 * se volvía a entrar con la misma cuenta vacía (§174).
 *
 * @param {string} [ctaId] Id declarado en `lib/ctaRegistry.js` con meta `connect`.
 * @param {string|null} [hintEmail] Email de la cuenta a reconectar.
 * @param {{ otraCuenta?: boolean }} [opciones]
 */
export function getGbusinessConnectUrl(ctaId, hintEmail = null, { otraCuenta = false } = {}) {
  const base = '/api/v1/gbusiness/auth/connect';
  const params = new URLSearchParams();
  if (ctaId) {
    params.set('cta', ctaId);
    // Solo la ruta, nunca `search`: puede llevar un `?redirect=` con una URL
    // privada. El servidor lo recorta igual, pero no hay motivo para mandarlo.
    const desde = typeof window !== 'undefined' ? window.location?.pathname : '';
    if (desde) params.set('from', desde);
  }
  if (hintEmail) params.set('hint', hintEmail);
  if (otraCuenta) params.set('otra', '1');
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Same-origin URL for a Google-hosted image (avatars, review photos).
 *
 * Needed only when the image has to be drawn into a `<canvas>`: Google's CDN
 * sends no CORS headers, so drawing it directly taints the canvas and
 * `toBlob()` throws. Plain `<img>` rendering does NOT need this — keep using
 * `referrerPolicy="no-referrer"` there.
 *
 * Returns a URL string (not a promise), same as `getGbusinessConnectUrl`.
 *
 * @param {string|null|undefined} url Original Google URL.
 * @returns {string|null} Proxied URL, or null when there's nothing to proxy.
 */
export function gbusinessImageProxyUrl(url) {
  if (!url) return null;
  return `/api/v1/gbusiness/image-proxy?url=${encodeURIComponent(url)}`;
}

// ---------------------------------------------------------------------------
// Accounts + locations (cached 15 min upstream)
// ---------------------------------------------------------------------------

export async function listGbusinessAccounts() {
  const { data } = await httpClient.get(`${BASE}/accounts`);
  return data.map((a) => ({
    accountId: a.account_id,
    accountName: a.account_name,
    accountType: a.account_type,
    verificationState: a.verification_state,
    vettedState: a.vetted_state,
    // Which OAuth connection (Google account email) owns this GBP account.
    // Lets the SPA group accounts/locations by source connection when the
    // user has more than one Google account linked.
    googleEmail: a.google_email || '',
  }));
}

export async function listGbusinessLocations(accountId) {
  const { data } = await httpClient.get(`${BASE}/accounts/${accountId}/locations`);
  return data.map((l) => ({
    locationId: l.location_id,
    accountId: l.account_id,
    title: l.title,
    storeCode: l.store_code,
    phoneNumber: l.phone_number,
    websiteUrl: l.website_url,
    address: l.address,
    primaryCategory: l.primary_category,
    additionalCategories: l.additional_categories,
    latitude: l.latitude,
    longitude: l.longitude,
    // 'google' | 'geocoded' | 'manual' | '' (fichas cacheadas antes del campo).
    // Distingue «Google dice dónde está» de «lo geocodificamos nosotros y nadie
    // lo ha mirado»: lo segundo puede caer en el portal de al lado, y el Mapa
    // SEO ofrece revisarlo antes de gastar créditos midiendo allí.
    coordsSource: l.coords_source || '',
    // Verificación en Google (metadata.hasVoiceOfMerchant/isVerified/
    // isSuspended). `canBeActivated=false` bloquea la selección en el
    // frontend — ver LocationSelector/SelectLocation.
    isVerified: l.is_verified,
    isSuspended: l.is_suspended,
    verificationState: l.verification_state,
    canBeActivated: l.can_be_activated,
  }));
}

// ---------------------------------------------------------------------------
// Reviews (paginated, server-side filters)
// ---------------------------------------------------------------------------

function _normalizeReview(r) {
  return {
    id: r.review_id,
    reviewId: r.review_id,
    accountId: r.account_id,
    locationId: r.location_id,
    locationName: r.location_name || '',
    locationMapsUri: r.location_maps_uri || '',
    rating: r.rating,
    comment: r.comment || '',
    commentTranslated: r.comment_translated || null,
    author: r.author || '',
    avatar: r.avatar || null,
    reply: r.reply
      ? { comment: r.reply.comment, updatedAt: r.reply.updated_at }
      : null,
    replyState: r.reply_state || null,
    media: Array.isArray(r.media) ? r.media : [],
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    autoReplied: !!r.auto_replied,
    autoRepliedAt: r.auto_replied_at || null,
    // Soft-deleted by the backend sweep when the review disappears from
    // Google's listing (the author retired it). The Reviews page shows
    // a "Retirada" badge and hides the reply button. See decisiones §59.
    retiredReview: !!r.retired_review,
    retiredAt: r.retired_at || null,
  };
}

/**
 * GET /gbusiness/reviews — paginated list.
 *
 * Params (all optional, snake_case as the backend expects):
 *   locationId, page (default 1), pageSize (default 20),
 *   rating (1-5), sentiment ('positive'|'neutral'|'negative'),
 *   replied (true|false), sort ('newest'|'oldest'),
 *   q (case-insensitive substring over comment/translated/author, ≤100 chars),
 *   force (bool, sync bypass).
 *
 * Returns { count, page, pageSize, nextPage, previousPage, results: Review[] }.
 */
/** @param {any} opts */
export async function listGbusinessReviews({
  locationIds, locationId, page = 1, pageSize = 20,
  rating, sentiment, replied, autoReplied, sort, q, force,
  retired, focusReviewId,
} = {}) {
  /** @type {Record<string, any>} */
  const params = { page, page_size: pageSize };
  const ids = _idsFrom({ locationIds, locationId });
  if (ids.length) params.location_id = ids;
  if (rating != null && rating !== 'all') params.rating = rating;
  if (sentiment && sentiment !== 'all') params.sentiment = sentiment;
  if (replied != null && replied !== 'all') params.replied = replied;
  if (autoReplied === true) params.auto_replied = 'true';
  if (sort) params.sort = sort;
  if (typeof q === 'string' && q.trim()) params.q = q.trim();
  if (force) params.force = 'true';
  // Retired tri-state (decisiones §59): true → only retired (the dedicated
  // tab); false → exclude retired (the replied/pending/auto tabs); omitted
  // → include both (the `all` tab — the only one where retired show mixed in).
  if (retired === true) params.retired = 'true';
  else if (retired === false) params.retired = 'false';
  // Deep-link de correos de alerta: el backend resuelve la página donde
  // cae la reseña y la devuelve en `focus_page`, sobrescribiendo el `page`
  // requested. Si no matchea filtros → `focus_page=null`.
  if (focusReviewId) params.focusReviewId = focusReviewId;

  const { data } = await httpClient.get(`${BASE}/reviews`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return {
    count: data.count,
    page: data.page,
    pageSize: data.page_size,
    nextPage: data.next_page,
    previousPage: data.previous_page,
    focusPage: data.focus_page ?? null,
    // Frescura de la caché del scope (TTL backend). `null`/ausente → asumimos
    // fresco (sin banner) para no sondear contra backends que aún no la envían.
    fresh: data.reviews_fresh ?? true,
    // ¿Hay un sync en vuelo para el scope? Señal para el spinner + polling
    // (a diferencia de `fresh`, sigue True hasta que la task termina todas las
    // páginas). El check verde se muestra con `fresh && !syncing`.
    syncing: data.syncing ?? false,
    results: (data.results || []).map(_normalizeReview),
  };
}

/**
 * Counts por estado para el filtro de la SPA:
 * `{all, replied, pending, auto, retired}`.
 *
 * Scope: location_id si se pasa; sin él, agregado del user (Pro) o el local
 * libre del Free user. Estables — no filtran por rating/sentiment/q.
 *
 * `all` INCLUYE retiradas (decisiones §59); `replied/pending/auto` las excluyen
 * porque el usuario eligió un estado accionable. `retired` es la pestaña dedicada.
 *
 * @param {{ locationId?: string }} [opts]
 */
export async function listGbusinessReviewsCounts({ locationIds, locationId } = {}) {
  /** @type {Record<string, any>} */
  const params = {};
  const ids = _idsFrom({ locationIds, locationId });
  if (ids.length) params.location_id = ids;
  const { data } = await httpClient.get(`${BASE}/reviews/counts`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return {
    all: data.all || 0,
    replied: data.replied || 0,
    pending: data.pending || 0,
    auto: data.auto || 0,
    retired: data.retired || 0,
  };
}

/**
 * Media EXACTA de estrellas del scope + los enteros que la componen:
 * `{ count, sum }`.
 *
 * Scope §67 (`location_id` repetido; sin ids = todos los locales activos).
 * Por diseño IGNORA los filtros de la SPA (rating/estado/sentimiento/keyword):
 * es la media del negocio, no la del subconjunto visible, así que su `count`
 * puede no coincidir con el contador de la lista. Excluye retiradas e ignora
 * rating 0, igual que el rating público de Google (§59).
 *
 * El consumidor calcula con `sum`/`count` (enteros) — el `average` del backend
 * se descarta a propósito para no arrastrar el error del float al decidir
 * cuántas reseñas de 5★ faltan para un objetivo. Ver `@/lib/ratingGoal`.
 *
 * @param {{ locationIds?: string[], locationId?: string }} [opts]
 */
export async function getGbusinessRatingStats({ locationIds, locationId } = {}) {
  /** @type {Record<string, any>} */
  const params = {};
  const ids = _idsFrom({ locationIds, locationId });
  if (ids.length) params.location_id = ids;
  const { data } = await httpClient.get(`${BASE}/reviews/rating-stats`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return {
    count: data.count || 0,
    sum: data.sum || 0,
  };
}

/**
 * Los datos del banner de fin del recorrido (`OnboardingDoneUpsell`): reseñas
 * de 4–5★ del mes natural en los locales activos y, si solo hay uno, su nombre.
 *
 * Sin parámetros: habla del negocio entero, no del local elegido en el selector.
 * `locationTitle` llega `null` con varios locales.
 */
export async function getGbusinessReviewsUpsellSummary() {
  const { data } = await httpClient.get(`${BASE}/reviews/upsell-summary`);
  return {
    positiveThisMonth: data.positive_this_month || 0,
    locationCount: data.location_count || 0,
    locationTitle: data.location_title || null,
  };
}

/**
 * Summary for Location Detail page: { count, averageRating }
 */
export async function getGbusinessReviewSummary(locationId) {
  const { data } = await httpClient.get(`${BASE}/reviews/summary`, {
    params: { location_id: locationId },
  });
  return {
    count: data.count || 0,
    averageRating: data.average_rating || 0,
  };
}

/**
 * Cuota mensual de envíos manuales del usuario. Pro siempre devuelve
 * `{used:0, limit:null, is_pro:true}` — la SPA usa eso para no renderizar
 * el counter. Free devuelve `{used, limit, is_pro:false}`.
 */
export async function getGbusinessManualReplyQuota() {
  const { data } = await httpClient.get(`${BASE}/reviews/manual-reply-quota`);
  return {
    used: data.used || 0,
    limit: data.limit ?? null,
    isPro: !!data.is_pro,
  };
}

/**
 * Cuota de publicaciones del plan free: UNA por inquilino y de POR VIDA (no al
 * mes). Existe para poder pintar el aviso antes de que el usuario escriba, en
 * vez de dejarle redactar la publicación y comerse un 402 al darle a publicar.
 *
 * Pro devuelve `{limit: null}` y la SPA usa eso para no pintar nada.
 */
export async function getGbusinessPublicationQuota() {
  const { data } = await httpClient.get(`${BASE}/publications/quota`);
  return {
    used: data.used || 0,
    limit: data.limit ?? null,
    remaining: data.remaining ?? null,
    isPro: !!data.is_pro,
  };
}

/**
 * Cuota mensual de GENERACIONES de respuesta con IA del usuario (distinta de
 * la de envíos manuales: cuenta cada vez que se pide a la IA que redacte).
 * Pro siempre `{used:0, limit:null, is_pro:true}` — la SPA usa eso para no
 * renderizar el banner. Free devuelve `{used, limit, is_pro:false}`.
 */
export async function getGbusinessAiReplyQuota() {
  const { data } = await httpClient.get(`${BASE}/reviews/ai-reply-quota`);
  return {
    used: data.used || 0,
    limit: data.limit ?? null,
    isPro: !!data.is_pro,
  };
}

/**
 * Loops `listGbusinessReviews` page by page (page_size 100, the backend cap)
 * until exhausted and returns the merged result list. Used by the dashboard
 * where we need totals + growth-vs-previous-period and the backend has no
 * aggregation endpoint yet.
 *
 * Devuelve `{ reviews, fresh }`: `fresh` viene de la primera página (frescura
 * del scope según el TTL backend) y deja al dashboard sondear solo mientras un
 * sync en segundo plano sigue en marcha.
 *
 * Safety cap: 50 pages (~5k reviews) — well above realistic usage; if a user
 * exceeds it we surface a warning in the console and stop.
 */
export async function listAllGbusinessReviews({ locationIds, locationId } = {}) {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 50;
  const all = [];
  let fresh = true;
  let syncing = false;
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { results, count, fresh: pageFresh, syncing: pageSyncing } =
      await listGbusinessReviews({
        // `retired: false` excluye las reseñas retiradas del dashboard: sus
        // totales, media, sentimiento y timeline son métricas y, por §59, las
        // retiradas no cuentan (igual que el rating público de Google). Solo la
        // pestaña dedicada de /reviews las muestra.
        locationIds, locationId, page, pageSize: PAGE_SIZE, retired: false,
      });
    if (page === 1) { fresh = pageFresh; syncing = pageSyncing; }
    all.push(...results);
    if (all.length >= count || results.length < PAGE_SIZE) break;
    if (page === MAX_PAGES) {
      console.warn(`listAllGbusinessReviews: capped at ${MAX_PAGES * PAGE_SIZE} reviews`);
    }
  }
  return { reviews: all, fresh, syncing };
}

/** POST owner reply (idempotent — overwrites if one exists). */
export async function replyGbusinessReview(reviewId, comment) {
  const { data } = await httpClient.post(
    `${BASE}/reviews/${reviewId}/reply`,
    { comment },
  );
  return _normalizeReview(data);
}

/** DELETE owner reply. */
export async function deleteGbusinessReviewReply(reviewId) {
  await httpClient.delete(`${BASE}/reviews/${reviewId}/reply`);
}

// ---------------------------------------------------------------------------
// Retroactive review backfill (feat/backfill_reviews). Paying-Pro only — the
// backend gates with IsPayingPro (402 for trialing). Reply publishing runs in
// a background Celery job; the UI polls its progress.
// ---------------------------------------------------------------------------

function _normalizeBackfillJob(data) {
  if (!data || data.status === 'none') return null;
  return {
    jobId: data.job_id || null,
    status: data.status,
    ratings: data.ratings,
    locationIds: Array.isArray(data.location_ids) ? data.location_ids : [],
    mode: data.mode,
    requested: data.requested || 0,
    processed: data.processed || 0,
    replied: data.replied || 0,
    failed: data.failed || 0,
    skipped: data.skipped || 0,
    errorCode: data.error_code || null,
    lastError: data.last_error || '',
  };
}

/**
 * POST /reviews/backfill — start a retroactive reply run.
 *
 * No lleva modo: un backfill responde siempre con IA.
 * @param {{limit:number, ratings:'4_5'|'all', locationIds:string[]}} opts
 *   locationIds: [] (or omitted) → every active location; else the given subset.
 * @returns {Promise<{jobId:string, requested:number}>}
 */
export async function startReviewsBackfill({ limit, ratings, locationIds }) {
  const { data } = await httpClient.post(`${BASE}/reviews/backfill`, {
    limit,
    ratings,
    location_ids: _idsFrom({ locationIds }),
  });
  return { jobId: data.job_id, requested: data.requested || 0 };
}

/** GET /reviews/backfill — the active-or-latest job (null if none yet). */
export async function getActiveReviewsBackfill() {
  const { data } = await httpClient.get(`${BASE}/reviews/backfill`);
  return _normalizeBackfillJob(data);
}

/** GET /reviews/backfill/<jobId> — poll one job's progress. */
export async function getReviewsBackfillJob(jobId) {
  const { data } = await httpClient.get(`${BASE}/reviews/backfill/${jobId}`);
  return _normalizeBackfillJob(data);
}

/**
 * GET /reviews/backfill/count — how many unanswered reviews match the filters
 * (star range + scope), and the cap the user can request. `locationIds` is
 * sent as repeated `location_id` params; [] → every active location.
 *
 * `hourlyLimit` es el techo de respuestas de IA por hora que el backfill
 * comparte con el auto-reply: por encima de ese número el trabajo NO cabe en
 * una pasada y la tarjeta lo avisa. Viene del servidor (es env-overridable)
 * para que el aviso no se dispare con un número que ya no es el vigente. 0 si
 * el backend aún no lo manda.
 * @returns {Promise<{count:number, max:number, hourlyLimit:number}>}
 */
export async function getReviewsBackfillCount({ ratings, locationIds } = {}) {
  /** @type {Record<string, any>} */
  const params = { ratings };
  const ids = _idsFrom({ locationIds });
  if (ids.length) params.location_id = ids;
  const { data } = await httpClient.get(`${BASE}/reviews/backfill/count`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return {
    count: data.count || 0,
    max: data.max || 0,
    hourlyLimit: data.hourly_limit || 0,
  };
}

// ---------------------------------------------------------------------------
// Location profile (PR #5 — full editor exposes more; the dashboard only
// needs read access to display address/phone/website + the maps URI.)
// ---------------------------------------------------------------------------

/**
 * GET /gbusiness/locations/<id>/profile — full Location detail from Google.
 * Returns the raw Google v1 shape (storefrontAddress, phoneNumbers,
 * categories, websiteUri, metadata.mapsUri, etc.).
 */
export async function getGbusinessLocationProfile(locationId) {
  const { data } = await httpClient.get(`${BASE}/locations/${locationId}/profile`);
  return data;
}

/**
 * PATCH /gbusiness/locations/{locationId}/profile
 *
 * Body: { updateMask: "field1,field2", data: {field1: ..., field2: ...} }
 *
 * `updateMask` is a comma-separated list of Google v1 field names — only those
 * are written. Keep it minimal: pass exactly the fields the user changed (the
 * editor tracks dirty per section). Returns the full updated Location doc.
 */
export async function updateGbusinessLocationProfile(locationId, updateMask, data) {
  const res = await httpClient.patch(
    `${BASE}/locations/${locationId}/profile`,
    { updateMask, data },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Protección de ficha (decisiones §152)
// ---------------------------------------------------------------------------

/**
 * GET /gbusiness/locations/{locationId}/protection
 *
 * Configuración y estado: `{enabled, groups, available_groups, status,
 * status_detail, last_checked_at, next_check_at, can_revert, mode,
 * feature_enabled, open_episodes}`. `can_revert` dice lo que recibe HOY la
 * ficha: `false` en un local sin Pro con asiento, que no se comprueba.
 */
export async function getGbusinessProtection(locationId) {
  const { data } = await httpClient.get(`${BASE}/locations/${locationId}/protection`);
  return data;
}

/** PUT /gbusiness/locations/{locationId}/protection — `{enabled, groups}`. */
export async function updateGbusinessProtection(locationId, { enabled, groups }) {
  const { data } = await httpClient.put(
    `${BASE}/locations/${locationId}/protection`, { enabled, groups },
  );
  return data;
}

/**
 * GET /gbusiness/locations/{locationId}/protection/events
 *
 * Historial de episodios, lo más nuevo primero, de 20 en 20. Para la página
 * siguiente se pasa el `next_before` que vino en la anterior.
 */
export async function listGbusinessProtectionEvents(locationId, { before } = {}) {
  const { data } = await httpClient.get(
    `${BASE}/locations/${locationId}/protection/events`,
    { params: before ? { before } : {} },
  );
  return data;
}

/**
 * POST …/protection/events/{eventId}/{decision} — `decision` es `accept`
 * (adoptar el valor de Google) o `restore` (reescribir el del dueño). Escribe
 * en Google: Pro y asiento.
 */
export async function resolveGbusinessProtectionEvent(locationId, eventId, decision) {
  const { data } = await httpClient.post(
    `${BASE}/locations/${locationId}/protection/events/${eventId}/${decision}`,
  );
  return data;
}

/**
 * POST …/protection/check — «comprobar ahora». No espera a Google: adelanta la
 * comprobación al siguiente tick del beat (≤ 1 min). 409 si no está activada.
 */
export async function checkGbusinessProtectionNow(locationId) {
  const { data } = await httpClient.post(`${BASE}/locations/${locationId}/protection/check`);
  return data;
}

/**
 * GET /gbusiness/locations/{locationId}/categories/suggestions
 *
 * Hasta 3 categorías secundarias sugeridas con IA (decisiones §183), siempre de
 * la lista de Google del país de la ficha. Devuelve
 * `{ suggestions: [{name, displayName, reason, source, count, confidence}],
 *    reason, lang, cached }`. `reason` dice por qué no hay ninguna
 * (`enough`, `no_category`, `no_taxonomy`, `none_fit`): en todos esos casos el
 * bloque simplemente no se pinta.
 */
export async function getCategorySuggestions(locationId) {
  const { data } = await httpClient.get(
    `${BASE}/locations/${locationId}/categories/suggestions`,
    // La primera generación son dos llamadas a OpenAI (una con fotos): 6–8 s
    // normalmente, y con OpenAI lento pasaba de los 15 s del cliente. El
    // backend la termina y la cachea igual, pero el bloque se quedaba en
    // error. Las siguientes salen de la caché al instante.
    { timeout: 45000 },
  );
  return {
    suggestions: Array.isArray(data?.suggestions) ? data.suggestions : [],
    reason: data?.reason || null,
  };
}

/**
 * GET /gbusiness/locations/{locationId}/attributes
 *
 * Returns `{ attributes: [{name, valueType, values, uriValues, ...}, ...] }`
 * directly from Google. Names look like `attributes/has_wifi`.
 */
export async function getGbusinessLocationAttributes(locationId) {
  const { data } = await httpClient.get(
    `${BASE}/locations/${locationId}/attributes`,
  );
  return data?.attributes || [];
}

/**
 * GET /gbusiness/locations/{locationId}/attributes/applicable
 *
 * Returns `{ ids: ["attributes/...", ...] }` — the subset of attributes
 * Google considers valid for this location's primary category. Used to filter
 * the static catalogue in `lib/gbpAttributes.js` so users only see toggles
 * their category supports (PATCHing an unsupported one yields 400).
 */
export async function getGbusinessLocationApplicableAttributes(locationId) {
  const { data } = await httpClient.get(
    `${BASE}/locations/${locationId}/attributes/applicable`,
  );
  return data?.ids || [];
}


/**
 * PUT /gbusiness/locations/{locationId}/attributes
 *
 * Body: `{ attributes, attributeIds }`. `attributeIds` is the explicit list of
 * `attributes/<id>` names being touched — required by Google's v1 PATCH
 * semantics. To delete an attribute, include its name in `attributeIds` but
 * omit it from `attributes`. Use `mergeAttributes()` from `lib/gbpAttributes`
 * to build both lists safely.
 */
export async function updateGbusinessLocationAttributes(locationId, attributes, attributeIds) {
  const res = await httpClient.put(
    `${BASE}/locations/${locationId}/attributes`,
    { attributes, attributeIds },
  );
  return res.data?.attributes || [];
}

/**
 * GET /gbusiness/categories/search?q=&languageCode=&regionCode=
 *
 * Autocomplete over the GBP categories taxonomy. Returns
 * `[{name, displayName}, ...]`. Sends `[]` for queries shorter than 2 chars.
 * Caller should debounce to avoid hammering the endpoint (recommended 250ms).
 */
export async function searchGbusinessCategories({ q, languageCode, regionCode } = {}) {
  if (!q || q.trim().length < 2) return [];
  const { data } = await httpClient.get(`${BASE}/categories/search`, {
    params: {
      q: q.trim(),
      ...(languageCode ? { languageCode } : {}),
      ...(regionCode ? { regionCode } : {}),
    },
  });
  return Array.isArray(data) ? data : [];
}

// ---------------------------------------------------------------------------
// Place Action Links — the prominent CTA buttons on a Maps listing
// (Reserve, Order online, etc.). Requires the mybusinessplaceactions API
// to be enabled per Cloud project: when it isn't, the backend returns
// HTTP 502 with code='place_actions_not_enabled' which the UI uses to
// render an "enable API" banner instead of a generic error toast.
// ---------------------------------------------------------------------------

export async function listGbusinessActionLinks(locationId) {
  const { data } = await httpClient.get(`${BASE}/locations/${locationId}/action-links`);
  return Array.isArray(data) ? data : [];
}

// Tipos de acción DISPONIBLES para el local (Google los filtra por la categoría
// de la ficha). La UI muestra solo estos en el desplegable para evitar el 400
// INVALID_LOCATION_CATEGORY. Devuelve `[{placeActionType, displayName}]`.
export async function listGbusinessActionLinkTypes(locationId, language) {
  const { data } = await httpClient.get(
    `${BASE}/locations/${locationId}/action-link-types`,
    { params: language ? { language } : undefined },
  );
  return Array.isArray(data) ? data : [];
}

export async function createGbusinessActionLink(
  locationId, { uri, placeActionType, isPreferred = false } = {},
) {
  const { data } = await httpClient.post(
    `${BASE}/locations/${locationId}/action-links`,
    { uri, placeActionType, isPreferred },
  );
  return data;
}

export async function updateGbusinessActionLink(
  locationId, linkId, patch,
) {
  const { data } = await httpClient.patch(
    `${BASE}/locations/${locationId}/action-links/${linkId}`,
    patch,
  );
  return data;
}

export async function deleteGbusinessActionLink(locationId, linkId) {
  await httpClient.delete(`${BASE}/locations/${locationId}/action-links/${linkId}`);
}

// ---------------------------------------------------------------------------
// Health Score (SEO Local)
// ---------------------------------------------------------------------------

/**
 * GET /gbusiness/health-score[?location_id=X]
 *
 * Returns `{ score, delta, locations, target_location_id }` where
 * `score` has the 6-category breakdown + next_actions. See
 * `gbusiness/services/health_score.py` for the exact shape.
 *
 * If the user has no cached locations, the backend returns `{ error: 'no_locations' }`
 * which we surface as a null result so the caller can render an empty state.
 */
export async function getGbusinessHealthScore(scope) {
  // `scope`: array de ids (multi §67), id escalar legacy, o vacío (todos).
  // El headline usa el primer local del scope; la comparación es completa.
  const ids = Array.isArray(scope)
    ? _idsFrom({ locationIds: scope })
    : _idsFrom({ locationId: scope });
  const params = ids.length ? { location_id: ids } : {};
  const { data } = await httpClient.get(`${BASE}/health-score`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  if (data && data.error) return null;
  return data;
}

/**
 * GET /gbusiness/health-score-v2[?location_id=X] — SEO Score v2 (§175, solo
 * staging). Como la v1 pero sin `delta` y con `positioning` (el escaneo del
 * Mapa SEO de la categoría principal, o null) y `primary_category`.
 */
export async function getGbusinessHealthScoreV2(locationId) {
  const ids = _idsFrom({ locationId });
  const params = ids.length ? { location_id: ids } : {};
  const { data } = await httpClient.get(`${BASE}/health-score-v2`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  if (data && data.error) return null;
  return data;
}

// ---------------------------------------------------------------------------
// Performance metrics (BusinessProfile Performance v1, cached 1h)
// ---------------------------------------------------------------------------

/**
 * GET /gbusiness/metrics — aggregated KPIs + growth + platforms breakdown.
 *
 * Params:
 *   locationId  (required)
 *   from, to    — Date objects (preferred) OR ISO strings; if absent uses `days`
 *   days        — fallback for shorthand (default 30)
 *   force       — bypass the 1h cache (refetch from Google)
 *
 * Returns the shape from `views/metrics.py`:
 *   {
 *     range:        { days, current: {start,end}, previous: {start,end} },
 *     totals:       { impressions, web, calls, directions },
 *     growth:       { impressions, web, calls, directions },   // pct ints or null
 *     daily:        [{ date, impressions, web, calls, directions }],
 *     platforms:    [{ key, value, percent }],
 *     capabilities: { impressions, web, calls, directions },
 *   }
 */
export async function getGbusinessMetrics({ locationIds, locationId, from, to, days, force } = {}) {
  // §67: acepta subconjunto (`locationIds`) o id único legacy. Sin ids el
  // backend agrega TODOS los locales (suma de KPIs, que son conteos).
  const ids = _idsFrom({ locationIds, locationId });
  /** @type {Record<string, any>} */
  const params = {};
  if (ids.length) params.location_id = ids;
  const toIso = (v) => (v instanceof Date ? v.toISOString().slice(0, 10) : v);
  if (from && to) {
    params.from = toIso(from);
    params.to = toIso(to);
  } else if (days != null) {
    params.days = days;
  }
  if (force) params.force = 'true';
  const { data } = await httpClient.get(`${BASE}/metrics`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return data;
}

/**
 * GET /gbusiness/search-keywords — búsquedas que mostraron el perfil en un mes.
 *
 * Pro y POR LOCAL con asiento: un único local sin asiento da 402
 * `seat_required`; con varios, el backend agrega solo los que lo llevan y
 * devuelve el resto en `lockedLocationIds`.
 *
 * Params:
 *   locationIds — subconjunto; vacío = todos los del scope por defecto
 *   month       — 'YYYY-MM'; sin él, el último mes cerrado
 *
 * Returns (ver `QR/gbusiness/services/search_keywords.py::build_payload`):
 *   {
 *     syncing, month, previousMonth, availableMonths: ['YYYY-MM', …],
 *     status, previousStatus,   // 'ready'|'pending'|'not_published'|'no_data'
 *     locationIds, lockedLocationIds, total,
 *     keywords: [{ keyword, value, threshold, approx, previousValue,
 *                  previousThreshold, change, isNew }],
 *   }
 * `value` null + `threshold` N quiere decir «menos de N», no cero.
 */
export async function getGbusinessSearchKeywords({ locationIds, month } = {}) {
  const ids = _idsFrom({ locationIds });
  /** @type {Record<string, any>} */
  const params = {};
  if (ids.length) params.location_id = ids;
  if (month) params.month = month;
  const { data } = await httpClient.get(`${BASE}/search-keywords`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Publications (LocalPosts) — live + scheduled queue (Celery beat publishes)
// ---------------------------------------------------------------------------

function _normalizePost(p) {
  return {
    postName: p.post_name,
    postId: p.post_id,
    summary: p.summary || '',
    topicType: p.topic_type || 'STANDARD',
    languageCode: p.language_code || '',
    media: Array.isArray(p.media) ? p.media.map((m) => ({
      googleUrl: m.google_url || null,
      sourceUrl: m.source_url || null,
      thumbnailUrl: m.thumbnail_url || null,
      // Google da una `googleUrl` de imagen también para los vídeos (§178).
      mediaFormat: m.media_format || 'PHOTO',
    })) : [],
    callToAction: p.call_to_action || null,
    state: p.state || '',
    searchUrl: p.search_url || null,
    event: p.event || null,
    offer: p.offer || null,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  };
}

function _normalizeScheduled(s) {
  return {
    id: s.id,
    locationId: s.location_id,
    topicType: s.topic_type,
    summary: s.summary,
    callToAction: s.call_to_action || null,
    mediaSourceUrl: s.media_source_url || null,
    event: s.event || null,
    offer: s.offer || null,
    scheduledAt: s.scheduled_at,
    status: s.status,
    publishedAt: s.published_at,
    publishedName: s.published_name,
    failedAt: s.failed_at,
    error: s.error,
    createdAt: s.created_at,
  };
}

/** GET /publications?location_id= — cached LocalPosts for one location. */
export async function listGbusinessPublications({ locationId, force } = {}) {
  if (!locationId) throw new Error('locationId is required');
  const params = { location_id: locationId };
  if (force) params.force = 'true';
  const { data } = await httpClient.get(`${BASE}/publications`, { params });
  return (data || []).map(_normalizePost);
}

/**
 * POST /publications — publish a LocalPost immediately.
 *
 * El único parámetro de media admitido es `mediaSourceUrl` (URL pública
 * que Google fetch). El path histórico `mediaUploadRef` se retiró cuando
 * Google v4 dejó de aceptar `dataRef.resourceName` para LocalPost media —
 * ver decisiones §60. La foto se sube vía `uploadGbusinessPostAttachment`,
 * que devuelve `publicUrl` lista para pasar aquí.
 */
export async function createGbusinessPublication({
  locationId, topicType = 'STANDARD', summary,
  callToAction, mediaSourceUrl, event, offer,
}) {
  const body = { location_id: locationId, topic_type: topicType, summary };
  if (callToAction?.actionType) {
    body.call_to_action = {
      actionType: callToAction.actionType,
      ...(callToAction.url ? { url: callToAction.url } : {}),
    };
  }
  if (mediaSourceUrl) body.media_source_url = mediaSourceUrl;
  // Google exige `event` (title + schedule) tanto para EVENT como para OFFER
  // (en las ofertas la vigencia va en `event.schedule`). `offer` es adicional.
  if ((topicType === 'EVENT' || topicType === 'OFFER') && event) body.event = event;
  if (topicType === 'OFFER' && offer) body.offer = offer;
  const { data } = await httpClient.post(`${BASE}/publications`, body);
  return data;
}

/**
 * Traduce un objeto de cambios camelCase → el body snake_case de un PATCH.
 *
 * SOLO se mapean las claves PRESENTES en `changes`: lo que no viaja, el backend
 * no lo mete en el `updateMask` y Google lo deja intacto. Mandar `null` sí
 * viaja y significa "quítalo" (es como se borra la foto o el botón). Por eso
 * aquí se usa `in` y no un chequeo de valor truthy como en el POST de crear.
 */
function _buildPostPatchBody(changes) {
  const body = {};
  if ('summary' in changes) body.summary = changes.summary;
  if ('callToAction' in changes) {
    body.call_to_action = changes.callToAction?.actionType
      ? {
        actionType: changes.callToAction.actionType,
        ...(changes.callToAction.url ? { url: changes.callToAction.url } : {}),
      }
      : null;
  }
  if ('mediaSourceUrl' in changes) body.media_source_url = changes.mediaSourceUrl || null;
  if ('event' in changes) body.event = changes.event || null;
  if ('offer' in changes) body.offer = changes.offer || null;
  return body;
}

/**
 * PATCH /publications/<post_id>?location_id= — editar una publicación viva.
 *
 * Google conserva el post (mismo enlace, misma fecha, mismas métricas); solo
 * cambian los campos enviados. El tipo de publicación NO se puede cambiar una
 * vez publicada — el backend lo ignora, igual que el editor de Google.
 */
export async function updateGbusinessPublication({ postId, locationId, ...changes }) {
  const { data } = await httpClient.patch(
    `${BASE}/publications/${encodeURIComponent(postId)}`,
    _buildPostPatchBody(changes),
    { params: { location_id: locationId } },
  );
  return _normalizePost(data);
}

/**
 * PATCH /publications/scheduled/<id> — editar una publicación programada.
 *
 * Solo toca Mongo (aún no existe nada en Google), así que aquí sí se pueden
 * cambiar el tipo y la fecha. Devuelve 409 `scheduled_not_pending` si el worker
 * ya la cogió para publicarla.
 */
export async function updateScheduledPublication(scheduledId, changes = {}) {
  const body = _buildPostPatchBody(changes);
  if ('topicType' in changes) body.topic_type = changes.topicType;
  if ('scheduledAt' in changes) body.scheduled_at = changes.scheduledAt;
  const { data } = await httpClient.patch(
    `${BASE}/publications/scheduled/${encodeURIComponent(scheduledId)}`,
    body,
  );
  return _normalizeScheduled(data);
}

/** DELETE /publications/<post_id>?location_id=. */
export async function deleteGbusinessPublication({ postId, locationId }) {
  await httpClient.delete(`${BASE}/publications/${encodeURIComponent(postId)}`, {
    params: { location_id: locationId },
  });
}

/** GET /publications/scheduled?location_id=. */
export async function listScheduledPublications({ locationId } = {}) {
  const params = {};
  if (locationId) params.location_id = locationId;
  const { data } = await httpClient.get(`${BASE}/publications/scheduled`, { params });
  return (data || []).map(_normalizeScheduled);
}

/**
 * POST /publications/scheduled — queue a scheduled post. `scheduledAt` is
 * an ISO 8601 string (UTC). Celery beat publishes it when due.
 */
export async function createScheduledPublication({
  locationId, topicType = 'STANDARD', summary, scheduledAt,
  callToAction, mediaSourceUrl, event, offer,
}) {
  const body = {
    location_id: locationId,
    topic_type: topicType,
    summary,
    scheduled_at: scheduledAt,
  };
  if (callToAction?.actionType) {
    body.call_to_action = {
      actionType: callToAction.actionType,
      ...(callToAction.url ? { url: callToAction.url } : {}),
    };
  }
  if (mediaSourceUrl) body.media_source_url = mediaSourceUrl;
  // Google exige `event` (title + schedule) tanto para EVENT como para OFFER
  // (en las ofertas la vigencia va en `event.schedule`). `offer` es adicional.
  if ((topicType === 'EVENT' || topicType === 'OFFER') && event) body.event = event;
  if (topicType === 'OFFER' && offer) body.offer = offer;
  const { data } = await httpClient.post(`${BASE}/publications/scheduled`, body);
  return _normalizeScheduled(data);
}

/** DELETE /publications/scheduled/<id>. */
export async function deleteScheduledPublication(scheduledId) {
  await httpClient.delete(
    `${BASE}/publications/scheduled/${encodeURIComponent(scheduledId)}`,
  );
}

// ---------------------------------------------------------------------------
// Profile media (location photos: logo/cover/additional). Cached 15 min.
// ---------------------------------------------------------------------------

function _normalizeMedia(m) {
  return {
    mediaName: m.media_name,
    locationId: m.location_id,
    googleUrl: m.google_url || null,
    thumbnailUrl: m.thumbnail_url || null,
    sourceUrl: m.source_url || null,
    mediaFormat: m.media_format || 'PHOTO',
    category: m.category || 'ADDITIONAL',
    description: m.description || '',
    width: m.width || null,
    height: m.height || null,
    createTime: m.create_time || null,
  };
}

/**
 * GET /locations/<id>/cover — la portada de un local (o la foto más reciente si
 * no tiene). A diferencia de `/media` no exige Pro: la usa el Mapa SEO, que
 * también se usa con créditos. `{googleUrl, thumbnailUrl}`, `null` si no hay.
 */
export async function getGbusinessLocationCover(locationId) {
  if (!locationId) throw new Error('locationId is required');
  const { data } = await httpClient.get(
    `${BASE}/locations/${encodeURIComponent(locationId)}/cover`,
  );
  return {
    googleUrl: data?.google_url || null,
    thumbnailUrl: data?.thumbnail_url || null,
  };
}

/** GET /media?location_id= — cached profile photos for a location. */
export async function listGbusinessMedia({ locationId, force } = {}) {
  if (!locationId) throw new Error('locationId is required');
  const params = { location_id: locationId };
  if (force) params.force = 'true';
  const { data } = await httpClient.get(`${BASE}/media`, { params });
  return (data || []).map(_normalizeMedia);
}

/**
 * POST /media/upload (multipart) — upload a binary file. Runs through
 * Pillow on the server (HEIC supported), reencoded to JPEG, then Google's
 * 3-step upload. Throttled 30/h.
 */
export async function uploadGbusinessMediaFile({
  locationId, file, category = 'ADDITIONAL', description = '',
}) {
  if (!locationId) throw new Error('locationId is required');
  if (!file) throw new Error('file is required');
  const form = new FormData();
  form.append('location_id', locationId);
  form.append('file', file);
  form.append('category', category);
  if (description) form.append('description', description);
  const { data } = await httpClient.post(`${BASE}/media/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return _normalizeMedia(data);
}

/**
 * POST /media/url — register a public URL as a profile media item.
 * Cheaper than the binary upload (Google fetches it). Throttled 30/h.
 */
export async function uploadGbusinessMediaFromUrl({
  locationId, sourceUrl, category = 'ADDITIONAL', description = '',
}) {
  if (!locationId) throw new Error('locationId is required');
  if (!sourceUrl) throw new Error('sourceUrl is required');
  const { data } = await httpClient.post(`${BASE}/media/url`, {
    location_id: locationId, source_url: sourceUrl, category, description,
  });
  return _normalizeMedia(data);
}

/**
 * Normaliza un job de subida masiva (fan-out async). El POST devuelve 202 con
 * este shape (todos los locales `pending`) y luego se hace polling del estado.
 * `{ jobId, status: 'processing'|'done', total, done, failed, items: [{locationId, status, code, detail}] }`
 */
function _normalizeBulkJob(d) {
  return {
    jobId: d.job_id,
    status: d.status || 'processing',
    total: d.total || 0,
    done: d.done || 0,
    failed: d.failed || 0,
    items: (Array.isArray(d.items) ? d.items : []).map((it) => ({
      locationId: it.location_id,
      status: it.status || 'pending',
      code: it.code || null,
      detail: it.detail || null,
    })),
  };
}

/**
 * POST /media/upload/bulk (multipart) — sube UNA foto a N locales de forma
 * ASÍNCRONA: la request sube la foto a R2 y encola un job de fan-out; devuelve
 * 202 al instante con el job. El caller hace polling con `getBulkMediaJobStatus`
 * hasta `status === 'done'`. Antes era síncrono y con muchos locales agotaba el
 * timeout de axios de 15s (incidente galeriasmadrid.es).
 */
export async function uploadGbusinessMediaFileBulk({
  locationIds, file, category = 'ADDITIONAL', description = '',
}) {
  if (!Array.isArray(locationIds) || !locationIds.length) throw new Error('locationIds is required');
  if (!file) throw new Error('file is required');
  const form = new FormData();
  form.append('location_ids', JSON.stringify(locationIds));
  form.append('file', file);
  form.append('category', category);
  if (description) form.append('description', description);
  const { data } = await httpClient.post(`${BASE}/media/upload/bulk`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return _normalizeBulkJob(data);
}

/** POST /media/url/bulk — igual que `uploadGbusinessMediaFileBulk` pero la foto
 * viene por URL pública. Devuelve un job (202) → polling. */
export async function uploadGbusinessMediaFromUrlBulk({
  locationIds, sourceUrl, category = 'ADDITIONAL', description = '',
}) {
  if (!Array.isArray(locationIds) || !locationIds.length) throw new Error('locationIds is required');
  if (!sourceUrl) throw new Error('sourceUrl is required');
  const { data } = await httpClient.post(`${BASE}/media/url/bulk`, {
    location_ids: locationIds, source_url: sourceUrl, category, description,
  });
  return _normalizeBulkJob(data);
}

/** GET /media/upload/bulk/<jobId> — progreso del fan-out async (polling). */
export async function getBulkMediaJobStatus(jobId) {
  const { data } = await httpClient.get(
    `${BASE}/media/upload/bulk/${encodeURIComponent(jobId)}`,
  );
  return _normalizeBulkJob(data);
}

/** DELETE /media/delete?name=accounts/X/locations/Y/media/Z. */
export async function deleteGbusinessMedia(mediaName) {
  await httpClient.delete(`${BASE}/media/delete`, {
    params: { name: mediaName },
  });
}

/**
 * POST /media/post-attachment — sube una foto al bucket R2 público
 * `tapstar-attachments-*` y devuelve la URL pública (estable hasta el
 * lifecycle de 180d). El caller la pasa como `mediaSourceUrl` al endpoint
 * de publish o scheduled.
 *
 * Reemplaza el flujo histórico de upload-ref de Google v4, que fue
 * retirado por Google en favor exclusivo de `sourceUrl` — decisiones §60.
 */
export async function uploadGbusinessPostAttachment({ locationId, file }) {
  if (!locationId) throw new Error('locationId is required');
  if (!file) throw new Error('file is required');
  const form = new FormData();
  form.append('location_id', locationId);
  form.append('file', file);
  const { data } = await httpClient.post(`${BASE}/media/post-attachment`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return {
    publicUrl: data.public_url,
    width: data.width,
    height: data.height,
    mediaFormat: 'PHOTO',
  };
}

/**
 * PUT directo del navegador a R2 con la URL firmada. Va por XHR y FUERA de
 * `httpClient` a propósito: es otro origen (no lleva cookie ni CSRF), el
 * timeout de 15 s de axios cortaría un vídeo de 75 MB por datos móviles, y XHR
 * es lo que da el progreso de subida. Las cabeceras tienen que ser EXACTAMENTE
 * las firmadas (el `Content-Length` lo pone el navegador a partir del File).
 */
function putToR2(url, file, headers, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(Object.assign(new Error(`R2 PUT ${xhr.status}`), { code: 'video_upload_failed' })));
    xhr.onerror = () => reject(Object.assign(new Error('R2 PUT network error'), { code: 'video_upload_failed' }));
    xhr.onabort = () => reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

/**
 * Vídeo de publicación (§178): firma → PUT directo a R2 → confirm. El backend
 * lee las cabeceras en `confirm` y, si el vídeo no cumple, lo borra y responde
 * 400 con el `code` del motivo (`video_codec`, `video_too_long`…).
 */
export async function uploadGbusinessPostVideo({ locationId, file, onProgress, signal }) {
  if (!locationId) throw new Error('locationId is required');
  if (!file) throw new Error('file is required');
  const { data: signed } = await httpClient.post(`${BASE}/media/post-attachment/video/presign`, {
    location_id: locationId,
    content_type: file.type,
    size: file.size,
  });
  await putToR2(signed.upload_url, file, signed.headers, { onProgress, signal });
  const { data } = await httpClient.post(`${BASE}/media/post-attachment/video/confirm`, {
    key: signed.key,
  });
  return {
    publicUrl: data.public_url,
    width: data.width,
    height: data.height,
    duration: data.duration,
    mediaFormat: 'VIDEO',
  };
}

/**
 * DELETE /media/post-attachment?public_url= — cleanup best-effort cuando el
 * publish/schedule falla TRAS un upload exitoso. Evita que la foto huérfana
 * se quede ocupando R2 hasta que el lifecycle (180d) la barra.
 *
 * Idempotente: si la URL ya fue borrada o no pertenece a nuestro bucket,
 * el backend devuelve 204 igualmente. Promise nunca rechaza al caller —
 * el cleanup es secundario al error principal del publish.
 */
export async function deleteGbusinessPostAttachment(publicUrl) {
  if (!publicUrl) return;
  try {
    await httpClient.delete(`${BASE}/media/post-attachment`, {
      params: { public_url: publicUrl },
    });
  } catch {
    // Silent: el lifecycle 180d cubre cualquier fallo aquí.
  }
}

// ---------------------------------------------------------------------------
// Sentiment-deep (AI cache; Celery-backed analysis; rate-limited 500/h 5000/d)
// ---------------------------------------------------------------------------

function _toIsoDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

// Serializa arrays como claves repetidas (`location_id=a&location_id=b`) para
// que casen con `request.query_params.getlist('location_id')` de DRF. Sin esto
// axios usaría `location_id[]=a`. Selección multi-local (decisiones §67).
export const REPEATED_PARAMS = { indexes: null };

// Normaliza el scope a una lista de location_ids. Acepta `locationIds` (array,
// contrato multi §67) y `locationId` (escalar legacy) para no romper callers
// aún sin migrar. Descarta vacíos y el centinela 'all' (= todos los locales).
function _idsFrom({ locationIds, locationId } = {}) {
  const arr = locationIds || (locationId != null ? [locationId] : []);
  return arr.filter((x) => x && x !== 'all');
}

function _scopeParams({ locationIds, locationId, from, to }) {
  // Backend trata "sin location_id" como "todos los locales del usuario".
  const params = {};
  const ids = _idsFrom({ locationIds, locationId });
  if (ids.length) params.location_id = ids;
  const fromIso = _toIsoDate(from);
  const toIso = _toIsoDate(to);
  if (fromIso) params.from = fromIso;
  if (toIso) params.to = toIso;
  return params;
}

/**
 * POST /gbusiness/sentiment-deep — returns the cache snapshot for the scope
 * AND enqueues Celery tasks for reviews missing/stale analysis. The frontend
 * fires this on page load; polling against /sentiment-deep/status drives the
 * progress UI until `pending === 0`.
 *
 * `locationId` may be 'all' / undefined to scope to every location.
 * `from`/`to` accept Date or ISO-date string ('YYYY-MM-DD'); both inclusive.
 *
 * Body:  { locationId?, from?, to?, force? }
 * Returns { analyses, pendingEnqueued, pendingTotal, rateLimit, code?, detail? }.
 * On 429 the rate-limit body is returned as-is (caller surfaces a toast).
 */
export async function kickoffSentimentDeep({
  locationIds, locationId, from, to, force = false,
} = {}) {
  // POST: el subconjunto va como `location_ids` (array) en el body (§67); el
  // backend mantiene `location_id` escalar legacy. Lista vacía = todos.
  const ids = _idsFrom({ locationIds, locationId });
  const body = { force };
  if (ids.length) body.location_ids = ids;
  const fromIso = _toIsoDate(from);
  const toIso = _toIsoDate(to);
  if (fromIso) body.from = fromIso;
  if (toIso) body.to = toIso;
  const { data } = await httpClient.post(`${BASE}/sentiment-deep`, body);
  return {
    analyses: data.analyses || [],
    pendingEnqueued: data.pending_enqueued || 0,
    pendingTotal: data.pending_total || 0,
    rateLimit: data.rate_limit || null,
    detail: data.detail,
    code: data.code,
  };
}

/**
 * GET /gbusiness/sentiment-deep/status — counts and rate-limit state for the
 * polling loop. Returns `{ totalReviews, analyzed, pending, inProgress,
 * lastAnalyzedAt, rateLimit }`.
 */
export async function getSentimentDeepStatus({ locationIds, locationId, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/sentiment-deep/status`, {
    params: _scopeParams({ locationIds, locationId, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    totalReviews: data.total_reviews || 0,
    analyzed: data.analyzed || 0,
    pending: data.pending || 0,
    inProgress: data.in_progress || 0,
    lastAnalyzedAt: data.last_analyzed_at || null,
    rateLimit: data.rate_limit || null,
  };
}

/**
 * GET /gbusiness/report-keywords — top keywords per sentiment + distribution
 * + coverage. Read-only, derived from `gbp_sentiment_cache`.
 *
 * Returns { counts, distribution, keywords: { positive, neutral, negative },
 *           coverage: { totalReviews, analyzed, pending } }.
 */
export async function getReportKeywords({ locationIds, locationId, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/report-keywords`, {
    params: _scopeParams({ locationIds, locationId, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    counts: data.counts || { positive: 0, neutral: 0, negative: 0 },
    distribution: data.distribution || { positive: 0, neutral: 0, negative: 0 },
    keywords: data.keywords || { positive: [], neutral: [], negative: [] },
    coverage: {
      totalReviews: data.coverage?.total_reviews || 0,
      analysable: data.coverage?.analysable || 0,
      analyzed: data.coverage?.analyzed || 0,
      pending: data.coverage?.pending || 0,
    },
  };
}

/**
 * GET /gbusiness/sentiment-metrics — NPS Real + aspect NPS + strength/challenge
 * + alerts + coverage.
 */
export async function getSentimentMetrics({ locationIds, locationId, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/sentiment-metrics`, {
    params: _scopeParams({ locationIds, locationId, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    npsReal: data.nps_real || {
      nps: null, promoters: 0, detractors: 0, neutral: 0, eligible: 0,
    },
    aspectNps: data.aspect_nps || [],
    strength: data.strength || null,
    challenge: data.challenge || null,
    urgentCount: data.urgent_count || 0,
    crisisCounts: data.crisis_counts || {},
    coverage: {
      totalReviews: data.coverage?.total_reviews || 0,
      analysable: data.coverage?.analysable || 0,
      analyzed: data.coverage?.analyzed || 0,
      pending: data.coverage?.pending || 0,
    },
  };
}

/**
 * GET /gbusiness/sentiment-comparison — per-location summary used by the
 * multi-location comparison card on ReportsNPS. Always all-locations scope
 * (no `locationId` arg). One row per location with at least one review in
 * the date range; the busiest location comes first.
 *
 * Returns:
 *   {
 *     locations: [{ locationId, title, total, positivePct, sentimentScore, nps }],
 *     coverage:  { totalReviews, analysable, analyzed, pending },
 *   }
 */
export async function getSentimentComparison({ locationIds, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/sentiment-comparison`, {
    params: _scopeParams({ locationIds, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    locations: (data.locations || []).map((l) => ({
      locationId: l.location_id,
      title: l.title || '',
      total: l.total || 0,
      positivePct: l.positive_pct || 0,
      sentimentScore: l.sentiment_score || 0,
      nps: l.nps,
    })),
    coverage: {
      totalReviews: data.coverage?.total_reviews || 0,
      analysable: data.coverage?.analysable || 0,
      analyzed: data.coverage?.analyzed || 0,
      pending: data.coverage?.pending || 0,
    },
  };
}

/**
 * GET /gbusiness/sentiment-evolution — weekly buckets keyed by ISO Monday.
 * Bounded payload (~52 buckets/year), pre-aggregated server-side.
 *
 * Returns { buckets: [{ date, positive, neutral, negative }], coverage }.
 */
export async function getSentimentEvolution({ locationIds, locationId, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/sentiment-evolution`, {
    params: _scopeParams({ locationIds, locationId, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    buckets: data.buckets || [],
    coverage: {
      totalReviews: data.coverage?.total_reviews || 0,
      analysable: data.coverage?.analysable || 0,
      analyzed: data.coverage?.analyzed || 0,
      pending: data.coverage?.pending || 0,
    },
  };
}

/**
 * GET /gbusiness/personal-keyword-scores — per-keyword 0-100 score derived
 * from the polarity of AI analyses where the LLM detected each configured
 * keyword (PR sentiment-5). Per-location only — `locationId='all'` is
 * rejected server-side because keywords are persisted per-location.
 *
 * Returns:
 *   {
 *     keywords: string[],                       // empty when not configured
 *     scores: { [kw]: { score, mentions } | null },
 *     coverage: { totalReviews, analysable, analyzed, pending },
 *   }
 */
export async function getPersonalKeywordScores({ locationIds, locationId, from, to } = {}) {
  const { data } = await httpClient.get(`${BASE}/personal-keyword-scores`, {
    params: _scopeParams({ locationIds, locationId, from, to }),
    paramsSerializer: REPEATED_PARAMS,
  });
  return {
    keywords: data.keywords || [],
    scores: data.scores || {},
    coverage: {
      totalReviews: data.coverage?.total_reviews || 0,
      analysable: data.coverage?.analysable || 0,
      analyzed: data.coverage?.analyzed || 0,
      pending: data.coverage?.pending || 0,
    },
  };
}

/**
 * GET /gbusiness/reviews-by-keyword — drill-down for the Keywords page.
 * Up to 10 most recent reviews that cite `keyword`, newest first. `tone`
 * (positive|negative|neutral) constrains to the clicked sentiment column.
 */
export async function getReviewsByKeyword({
  locationIds, locationId, from, to, keyword, tone,
} = {}) {
  const params = { ..._scopeParams({ locationIds, locationId, from, to }), keyword };
  if (tone) params.tone = tone;
  const { data } = await httpClient.get(`${BASE}/reviews-by-keyword`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return {
    keyword: data.keyword || keyword,
    tone: data.tone || null,
    results: (data.results || []).map(_normalizeReview),
  };
}

/**
 * POST /gbusiness/customer-themes — bulk LLM narrative "loves/improves"
 * over the reviews in scope. Cache-first: returns instantly when the
 * cache key (locations + dates + max_review_created_at + prompt_version +
 * model + lang) matches; otherwise calls OpenAI gpt-4o-mini and upserts.
 *
 * Pass `force: true` to bypass cache (consumes one rate-limit slot).
 *
 * Returns:
 *   {
 *     themes: { loves: [{title, description}], improves: [...] },
 *     scope:  { locationIds, fromDate, toDate, reviewCountUsed,
 *               totalReviewCount, maxReviewCreatedAt },
 *     cached: boolean,
 *     generatedAt: ISO string,
 *     model, promptVersion, lang,
 *     costUsd: number,
 *     rateLimit: { hourRemaining, hourResetAt },
 *   }
 * On HTTP 429 returns { code: 'themes_rate_limit_exceeded', rateLimit, detail }.
 */
export async function getCustomerThemes({
  locationIds, locationId, from, to, force = false,
} = {}) {
  // POST: subconjunto como `location_ids` (array) en el body (§67).
  const ids = _idsFrom({ locationIds, locationId });
  const body = { force };
  if (ids.length) body.location_ids = ids;
  const fromIso = _toIsoDate(from);
  const toIso = _toIsoDate(to);
  if (fromIso) body.from = fromIso;
  if (toIso) body.to = toIso;
  const { data } = await httpClient.post(`${BASE}/customer-themes`, body);
  if (data.code === 'themes_rate_limit_exceeded') {
    return {
      themes: { loves: [], improves: [] },
      cached: false,
      rateLimit: data.rate_limit || null,
      code: data.code,
      detail: data.detail,
    };
  }
  return {
    themes: data.themes || { loves: [], improves: [] },
    scope: {
      locationIds: data.scope?.location_ids || [],
      fromDate: data.scope?.from_date || null,
      toDate: data.scope?.to_date || null,
      maxReviewCreatedAt: data.scope?.max_review_created_at || null,
      reviewCountUsed: data.scope?.review_count_used || 0,
      totalReviewCount: data.scope?.total_review_count || 0,
    },
    cached: !!data.cached,
    // true while a Celery task is generating this scope (HTTP 202). The
    // caller polls by re-fetching until `generating` is false.
    generating: !!data.generating,
    generatedAt: data.generated_at || null,
    model: data.model || null,
    promptVersion: data.prompt_version || null,
    lang: data.lang || null,
    costUsd: data.cost_usd || 0,
    rateLimit: data.rate_limit || null,
  };
}

/**
 * Narrativa con IA sobre las métricas del periodo.
 *
 * Mismo contrato que `getCustomerThemes`: en cache-miss el backend encola y
 * responde 202 `{generating:true}`, así que quien llama debe repetir hasta que
 * `generating` sea false. El 429 se devuelve como objeto normal y no como
 * excepción, para que la UI pueda explicarlo en su sitio.
 *
 * NO se le manda el payload de métricas: el backend lo recalcula. Sería entrada
 * no confiable alimentando una llamada de pago.
 */
export async function getMetricsInsights({
  locationIds, locationId, from, to, force = false,
} = {}) {
  const ids = _idsFrom({ locationIds, locationId });
  const body = { force };
  if (ids.length) body.location_ids = ids;
  const fromIso = _toIsoDate(from);
  const toIso = _toIsoDate(to);
  if (fromIso) body.from = fromIso;
  if (toIso) body.to = toIso;

  const { data } = await httpClient.post(`${BASE}/metrics-insights`, body);
  const empty = { headline: '', findings: [], actions: [] };
  if (data.code === 'insights_rate_limit_exceeded') {
    return {
      insights: empty,
      cached: false,
      generating: false,
      rateLimit: data.rate_limit || null,
      code: data.code,
      detail: data.detail,
    };
  }
  return {
    insights: data.insights || empty,
    scope: {
      locationIds: data.scope?.location_ids || [],
      fromDate: data.scope?.from_date || null,
      toDate: data.scope?.to_date || null,
      days: data.scope?.days || null,
      compare: !!data.scope?.compare,
    },
    cached: !!data.cached,
    generating: !!data.generating,
    generatedAt: data.generated_at || null,
    model: data.model || null,
    promptVersion: data.prompt_version || null,
    lang: data.lang || null,
    costUsd: data.cost_usd || 0,
    rateLimit: data.rate_limit || null,
  };
}

/**
 * Narrativa con IA sobre el Health Score de SEO Local.
 *
 * Mismo contrato que `getMetricsInsights`: en cache-miss el backend encola y
 * responde 202 `{generating:true}`, así que quien llama debe repetir hasta que
 * `generating` sea false. El 429 se devuelve como objeto normal y no como
 * excepción, para que la UI pueda explicarlo en su sitio.
 *
 * NO se le manda el score: el backend lo recalcula. Sería entrada no confiable
 * alimentando una llamada de pago.
 *
 * `formula: 'v2'` narra el SEO Score v2 (§175, solo staging; en producción el
 * backend responde 404). Por defecto, la fórmula oficial.
 */
export async function getSeoInsights({ locationId, force = false, formula = 'v1' } = {}) {
  const body = { force };
  if (locationId && locationId !== 'all') body.location_id = locationId;
  if (formula === 'v2') body.formula = 'v2';

  const { data } = await httpClient.post(`${BASE}/seo-insights`, body);
  const empty = { headline: '', findings: [], actions: [] };
  if (data.code === 'seo_insights_rate_limit_exceeded') {
    return {
      insights: empty,
      cached: false,
      generating: false,
      rateLimit: data.rate_limit || null,
      code: data.code,
      detail: data.detail,
    };
  }
  return {
    insights: data.insights || empty,
    scope: {
      locationId: data.scope?.location_id || null,
      total: data.scope?.total ?? null,
      locationCount: data.scope?.location_count || 0,
    },
    cached: !!data.cached,
    generating: !!data.generating,
    generatedAt: data.generated_at || null,
    model: data.model || null,
    promptVersion: data.prompt_version || null,
    lang: data.lang || null,
    costUsd: data.cost_usd || 0,
    rateLimit: data.rate_limit || null,
  };
}

// ---------------------------------------------------------------------------
// AI (OpenAI-backed; throttled per-user — default 10/hour). reply/post now run
// async (decisiones §42): the POST enqueues a Celery job and returns 202
// {job_id}; we poll GET /ai/jobs/<id> until the result is ready. This keeps a
// slow OpenAI response off the Django request path.
// ---------------------------------------------------------------------------

/**
 * Turn a job `error_code` into an error shaped like an axios error so existing
 * mutation `onError` handlers (which read `e.response.status` / `.data.code`)
 * work unchanged whether the failure happened at enqueue or during the job.
 */
function aiJobError(errorCode) {
  const quotaCodes = [
    'free_auto_reply_quota_exceeded',
    'pro_auto_reply_quota_exceeded',
    'ai_quota_exceeded',
  ];
  const httpStatus = quotaCodes.includes(errorCode) ? 402 : 502;
  const err = new Error(errorCode || 'ai_service_error');
  err.response = { status: httpStatus, data: { code: errorCode || 'ai_service_error' } };
  return err;
}

/**
 * Poll GET /gbusiness/ai/jobs/<jobId> until the job leaves `pending`.
 * Resolves with the job `result` dict, or rejects with an axios-shaped error
 * (job-level failure → mapped via aiJobError; timeout → 504 ai_job_timeout).
 */
export async function pollAiJob(jobId, { intervalMs = 2000, timeoutMs = 60000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const { data } = await httpClient.get(`${BASE}/ai/jobs/${jobId}`);
    if (data.status === 'done') return data.result || {};
    if (data.status === 'error') throw aiJobError(data.error_code);
    if (Date.now() >= deadline) {
      const err = new Error('ai_job_timeout');
      err.response = { status: 504, data: { code: 'ai_job_timeout' } };
      throw err;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/**
 * POST /gbusiness/ai/reply → enqueue + poll. Returns
 * { reply, reviewLang, businessLang, tokensIn, tokensOut, costUsd, model }. The
 * enqueue POST throws on 402 (quota) / 429 (throttle); the poll throws on job
 * failure / timeout.
 */
export async function generateGbusinessReply(reviewId) {
  const { data } = await httpClient.post(`${BASE}/ai/reply`, { review_id: reviewId });
  const result = await pollAiJob(data.job_id);
  return {
    reply: result.reply,
    // ISO 639-1 code of the review's language (model-detected). The Reviews
    // page compares it against the business's language to decide whether to
    // offer the translation toggle / pre-send language modal. Comes back empty
    // for a review with no text: no comment, no language, nothing to decide.
    reviewLang: result.review_lang || null,
    // ISO 639-1 del idioma en que atiende el negocio (el `languageCode` de su
    // ficha de Google, o su país). Es el idioma en que el backend ha redactado
    // el borrador cuando la reseña no daba pistas, y por tanto lo que «mi
    // idioma» tiene que significar al publicar.
    businessLang: result.business_lang || null,
    tokensIn: result.tokens_in,
    tokensOut: result.tokens_out,
    costUsd: result.cost_usd,
    model: result.model,
  };
}

/**
 * POST /gbusiness/ai/translate → enqueue + poll. Translates a batch of texts
 * into `targetLang` (the owner's UI language). `items` is an array of
 * { key, text }; empty texts come back as ''. Returns a `{ key: translated }`
 * map. Backs the Reviews "Traducido" toggle and the pre-send language modal.
 * @param {Array<{key: string, text: string}>} items
 * @param {string} targetLang
 * @param {string} [sourceLang]
 * @returns {Promise<Record<string, string>>}
 */
export async function translateGbusinessText(items, targetLang, sourceLang) {
  const { data } = await httpClient.post(`${BASE}/ai/translate`, {
    items,
    target_lang: targetLang,
    ...(sourceLang ? { source_lang: sourceLang } : {}),
  });
  const result = await pollAiJob(data.job_id);
  return result.translations || {};
}

/**
 * POST /gbusiness/ai/post → enqueue + poll. Returns
 * { summary, tokensIn, tokensOut, costUsd, model }.
 *
 * `mediaSourceUrl`: URL pública en R2 de la foto que acompañará al post
 * (la que devuelve `uploadGbusinessPostAttachment`). Si viene, la IA la mira
 * y escribe un texto coherente con lo que se ve. El backend solo acepta URLs
 * de nuestro bucket — cualquier otra la rechaza con 400.
 */
export async function generateGbusinessPostSummary({
  locationId, topicType = 'STANDARD', theme = '', variables = [], multi = false,
  mediaSourceUrl = '',
}) {
  const { data } = await httpClient.post(`${BASE}/ai/post`, {
    location_id: locationId, topic_type: topicType, theme, variables,
    multi_location: multi,
    ...(mediaSourceUrl ? { media_source_url: mediaSourceUrl } : {}),
  });
  const result = await pollAiJob(data.job_id);
  return {
    summary: result.summary,
    tokensIn: result.tokens_in,
    tokensOut: result.tokens_out,
    costUsd: result.cost_usd,
    model: result.model,
  };
}

/**
 * Paso 4 del asistente de publicaciones: tres propuestas de texto. El backend
 * les da como contexto la foto, la ficha de Google, la web del negocio y los
 * detalles del evento/oferta (`event`/`offer`, con la forma de Google).
 *
 * @returns {Promise<Array<{angle: string, text: string}>>}
 */
export async function generateGbusinessPostSuggestions({
  locationId, topicType = 'STANDARD', theme = '', variables = [], multi = false,
  mediaSourceUrl = '', event, offer,
}) {
  const { data } = await httpClient.post(`${BASE}/ai/post/suggestions`, {
    location_id: locationId, topic_type: topicType, theme, variables,
    multi_location: multi,
    ...(mediaSourceUrl ? { media_source_url: mediaSourceUrl } : {}),
    ...(event ? { event } : {}),
    ...(offer ? { offer } : {}),
  });
  // Más margen que el minuto por defecto: la primera vez el job lee además la
  // web del negocio (la caché la ahorra en las siguientes).
  const result = await pollAiJob(data.job_id, { timeoutMs: 90000 });
  return result.suggestions || [];
}

/**
 * Paso 5 del asistente: ajuste rápido del texto con IA.
 *
 * @param {{locationId: string, text: string, adjustment: 'closer'|'formal'|'no_emojis'|'add_emojis'}} args
 * @returns {Promise<string>} el texto reescrito
 */
export async function adjustGbusinessPostText({ locationId, text, adjustment }) {
  const { data } = await httpClient.post(`${BASE}/ai/post/adjust`, {
    location_id: locationId, text, adjustment,
  });
  const result = await pollAiJob(data.job_id);
  return result.summary;
}

// ---------------------------------------------------------------------------
// Settings (brand tone, multi-tone per location, emojis, dos/donts)
// ---------------------------------------------------------------------------
//
// The backend persists `tones` as an array. Each tone scopes itself either
// to every location (applied_locations = []) or to specific ones. The
// selector resolves "specific > global"; the backend rejects two globals
// or a location appearing in two tones.
// All fields are snake_case on the wire, camelCase in the SPA.

export const EMPTY_TONE = {
  name: '',
  // Campos del formulario legacy (v1) — ya no se editan desde la SPA, solo se
  // muestran de forma read-only cuando un tono los trae de antes. Ver
  // `instructions` más abajo.
  adjectives: [],
  writingStyle: '',
  example: '',
  dos: [],
  donts: [],
  // Campo único del formulario nuevo (v2), sustituye a los de arriba.
  instructions: '',
  signature: '',
  appliedLocations: [],
  // 'high' → reseñas de 4-5★, 'low' → reseñas de 1-3★.
  ratingBucket: 'high',
  emojis: { enabled: false, type: 'auto', frequency: 'moderate', fixed: [] },
};

function _toneFromBackend(t) {
  if (!t) return { ...EMPTY_TONE };
  return {
    name: t.name || '',
    adjectives: t.adjectives || [],
    writingStyle: t.writing_style || '',
    example: t.example || '',
    dos: t.dos || [],
    donts: t.donts || [],
    instructions: t.instructions || '',
    // Derivado por el backend (ver PROMPT_VERSION_* en settings_serializers.py)
    // — de solo lectura, no se reenvía en el PUT.
    promptVersion: t.prompt_version || 1,
    signature: t.signature || '',
    appliedLocations: t.applied_locations || [],
    ratingBucket: t.rating_bucket || 'high',
    emojis: {
      enabled: !!t.emojis?.enabled,
      type: t.emojis?.type || 'auto',
      frequency: t.emojis?.frequency || 'moderate',
      fixed: t.emojis?.fixed || [],
    },
  };
}

function _toneToBackend(t) {
  return {
    name: t.name || '',
    adjectives: t.adjectives || [],
    writing_style: t.writingStyle || '',
    example: t.example || '',
    dos: t.dos || [],
    donts: t.donts || [],
    instructions: t.instructions || '',
    signature: t.signature || '',
    applied_locations: t.appliedLocations || [],
    rating_bucket: t.ratingBucket || 'high',
    emojis: {
      enabled: !!t.emojis?.enabled,
      type: t.emojis?.type || 'auto',
      frequency: t.emojis?.frequency || 'moderate',
      fixed: t.emojis?.fixed || [],
    },
  };
}

// ---------------------------------------------------------------------------
// Automations — three rule lists + server-managed stats counter
// ---------------------------------------------------------------------------

// Cobertura de una regla guardada antes de que las estrellas fueran
// configurables. Espejo de DEFAULT_REPLY_RATINGS en el backend.
const DEFAULT_REPLY_RATINGS = [4, 5];

/** @param {any} r */
function _autoReplyRuleFromBackend(r) {
  return {
    locationId:   r.location_id,
    enabled:      !!r.enabled,
    ratings:      r.ratings?.length ? r.ratings : [...DEFAULT_REPLY_RATINGS],
    mode:         r.mode || 'ai',
    message:      r.message || '',
    messageLow:   r.message_low || '',
    delayMinutes: r.delay_minutes ?? 0,
    delayRandom:  !!r.delay_random,
    delayMinMinutes: r.delay_min_minutes ?? 0,
    delayMaxMinutes: r.delay_max_minutes ?? 0,
    toneName:     r.tone_name || '',
    replyInReviewLang: !!r.reply_in_review_lang,
    enabledAt:    r.enabled_at || null,
  };
}

/** @param {any} r */
function _autoReplyRuleToBackend(r) {
  return {
    location_id:   r.locationId,
    enabled:       !!r.enabled,
    ratings:       r.ratings || [...DEFAULT_REPLY_RATINGS],
    mode:          r.mode || 'ai',
    message:       r.message || '',
    message_low:   r.messageLow || '',
    delay_minutes: r.delayMinutes ?? 0,
    delay_random:  !!r.delayRandom,
    delay_min_minutes: r.delayMinMinutes ?? 0,
    delay_max_minutes: r.delayMaxMinutes ?? 0,
    tone_name:     r.toneName || '',
    reply_in_review_lang: !!r.replyInReviewLang,
  };
}

/**
 * Coalesce backend `emails` (list) with the legacy single `email` into a
 * deduped array. Rules saved before multi-recipient support only carry
 * `email`, so this keeps them rendering their existing address.
 * @param {any} r
 * @returns {string[]}
 */
function _emailsFromBackend(r) {
  const list = [...(r.emails || []), ...(r.email ? [r.email] : [])];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const addr = (raw || '').trim();
    const key = addr.toLowerCase();
    if (addr && !seen.has(key)) {
      seen.add(key);
      out.push(addr);
    }
  }
  return out;
}

/** @param {any} r */
function _emailKeywordRuleFromBackend(r) {
  const emails = _emailsFromBackend(r);
  return {
    locationId: r.location_id,
    enabled:    !!r.enabled,
    emails,
    email:      emails[0] || '',
    keywords:   r.keywords || [],
    // Solo significa algo en la regla 'all' (el backend lo fuerza a false en
    // las demas): con el on, la regla global TAMBIEN cubre los locales que
    // tienen regla propia, sumando destinatarios y criterios.
    mergeWithSpecific: !!r.merge_with_specific,
    enabledAt:  r.enabled_at || null,
  };
}

/** @param {any} r */
function _emailKeywordRuleToBackend(r) {
  return {
    location_id: r.locationId,
    enabled:     !!r.enabled,
    emails:      r.emails || [],
    keywords:    r.keywords || [],
    merge_with_specific: !!r.mergeWithSpecific,
  };
}

/** @param {any} r */
function _emailRatingRuleFromBackend(r) {
  const emails = _emailsFromBackend(r);
  return {
    locationId: r.location_id,
    enabled:    !!r.enabled,
    emails,
    email:      emails[0] || '',
    ratings:    r.ratings || [],
    mergeWithSpecific: !!r.merge_with_specific,
    enabledAt:  r.enabled_at || null,
  };
}

/** @param {any} r */
function _emailRatingRuleToBackend(r) {
  return {
    location_id: r.locationId,
    enabled:     !!r.enabled,
    emails:      r.emails || [],
    ratings:     r.ratings || [],
    merge_with_specific: !!r.mergeWithSpecific,
  };
}

export async function getAutomations() {
  const { data } = await httpClient.get(`${BASE}/automations`);
  return {
    autoReply4And5Stars: (data.auto_reply_4_5_stars || []).map(_autoReplyRuleFromBackend),
    emailKeywordAlert:   (data.email_keyword_alert || []).map(_emailKeywordRuleFromBackend),
    emailRatingAlert:    (data.email_rating_alert || []).map(_emailRatingRuleFromBackend),
    autoReplyStats:      { total: data.auto_reply_stats?.total || 0 },
  };
}

/**
 * GET /automations/auto-reply/pending — respuestas automáticas en cola que aún
 * no se han enviado (esperando su retardo, o atascadas). Ampliar las estrellas
 * de la regla reinicia su ventana y se pierden justo esas, así que la tarjeta
 * enseña el número antes de guardar. `locationIds` va como `location_id`
 * repetido; [] → todas las reglas activas.
 * @returns {Promise<number>}
 */
export async function getAutoReplyPendingCount({ locationIds } = {}) {
  /** @type {Record<string, any>} */
  const params = {};
  // OJO: aquí NO vale `_idsFrom`, que descarta el centinela 'all'. En el resto
  // de endpoints 'all' significa "no filtres", pero una regla de automatización
  // se guarda literalmente con `location_id: 'all'`, así que hay que enviarlo:
  // si se descarta, la petición va sin scope y el backend cuenta TODAS las
  // reglas activas en vez de la del ámbito, inflando el número.
  const ids = (locationIds || []).filter(Boolean);
  if (ids.length) params.location_id = ids;
  const { data } = await httpClient.get(`${BASE}/automations/auto-reply/pending`, {
    params, paramsSerializer: REPEATED_PARAMS,
  });
  return data.pending || 0;
}

/**
 * Save automations. `partial` is camelCase {autoReply4And5Stars?,
 * emailKeywordAlert?, emailRatingAlert?}. Only the keys you pass are
 * replaced — the others are left as they are on the server. `enabledAt`
 * is server-managed and cannot be set by the client.
 */
/** @param {any} partial */
export async function saveAutomations(partial) {
  const payload = {};
  if (Array.isArray(partial.autoReply4And5Stars)) {
    payload.auto_reply_4_5_stars = partial.autoReply4And5Stars.map(_autoReplyRuleToBackend);
  }
  if (Array.isArray(partial.emailKeywordAlert)) {
    payload.email_keyword_alert = partial.emailKeywordAlert.map(_emailKeywordRuleToBackend);
  }
  if (Array.isArray(partial.emailRatingAlert)) {
    payload.email_rating_alert = partial.emailRatingAlert.map(_emailRatingRuleToBackend);
  }
  const { data } = await httpClient.put(`${BASE}/automations`, payload);
  return {
    autoReply4And5Stars: (data.auto_reply_4_5_stars || []).map(_autoReplyRuleFromBackend),
    emailKeywordAlert:   (data.email_keyword_alert || []).map(_emailKeywordRuleFromBackend),
    emailRatingAlert:    (data.email_rating_alert || []).map(_emailRatingRuleFromBackend),
    autoReplyStats:      { total: data.auto_reply_stats?.total || 0 },
  };
}

export async function getGbusinessSettings() {
  const { data } = await httpClient.get(`${BASE}/settings`);
  return {
    brandTone: data.brand_tone || '',
    tones: (data.tones || []).map(_toneFromBackend),
    locationSettings: data.location_settings || {},
    negativeReviewEmails: data.negative_review_emails || {},
    activatedLocations: data.activated_locations || [],
    // 'ask' = preguntar cada vez con el modal de idioma (default del backend).
    replyLanguageMode: data.reply_language_mode || 'ask',
  };
}

/**
 * Save settings. `partial` is a partial update — caller can pass any subset
 * of {brandTone, tones, locationSettings, negativeReviewEmails,
 * activatedLocations, replyLanguageMode}. We fetch-merge-put to avoid wiping
 * fields not currently being edited.
 *
 * When passing `tones`, send the FULL array (a create/edit/delete on a
 * single tone is done by reading getGbusinessSettings(), mutating the
 * array in memory, and putting it back). Same pattern for
 * `activatedLocations`.
 */
/** @param {any} partial */
export async function saveGbusinessSettings(partial) {
  const current = await getGbusinessSettings();
  const merged = { ...current, ...partial };
  const payload = {
    brand_tone: merged.brandTone || '',
    tones: (merged.tones || []).map(_toneToBackend),
    location_settings: merged.locationSettings || {},
    negative_review_emails: merged.negativeReviewEmails || {},
    activated_locations: merged.activatedLocations || [],
    reply_language_mode: merged.replyLanguageMode || 'ask',
  };
  const { data } = await httpClient.put(`${BASE}/settings`, payload);
  return {
    brandTone: data.brand_tone || '',
    tones: (data.tones || []).map(_toneFromBackend),
    locationSettings: data.location_settings || {},
    negativeReviewEmails: data.negative_review_emails || {},
    activatedLocations: data.activated_locations || [],
    replyLanguageMode: data.reply_language_mode || 'ask',
  };
}

// ---------------------------------------------------------------------------
// Reports — monthly PDF reports per location.
// ---------------------------------------------------------------------------

/** @param {any} r */
function _reportFromBackend(r) {
  return {
    id: r.id,
    reportType: r.report_type || 'monthly',
    googleEmail: r.google_email || '',
    locationId: r.location_id,
    locationTitle: r.location_title || '',
    locationAddress: r.location_address || '',
    year: r.year,
    month: r.month,
    periodLabel: r.period_label,
    status: r.status,
    statusDetail: r.status_detail || null,
    generatedAt: r.generated_at,
    expiresAt: r.expires_at,
    sizeBytes: r.size_bytes,
    sourceData: r.source_data || {},
  };
}

/**
 * GET /reports — list the caller's reports (filter optional).
 * @param {{ locationId?: string, year?: number, month?: number }} [filters]
 */
export async function listGbusinessReports(filters = {}) {
  const params = {};
  if (filters.locationId) params.location_id = filters.locationId;
  if (filters.year != null) params.year = filters.year;
  if (filters.month != null) params.month = filters.month;
  const { data } = await httpClient.get(`${BASE}/reports`, { params });
  return (data || []).map(_reportFromBackend);
}

/**
 * POST /reports — request generation for one (location, year, month).
 * Backend returns 201 with status='generating' OR 200 with the existing
 * doc when the period was already produced (idempotent).
 * @param {{ locationId: string, year: number, month: number }} input
 */
export async function generateGbusinessReport({ locationId, year, month }) {
  const { data } = await httpClient.post(`${BASE}/reports`, {
    location_id: locationId,
    year,
    month,
  });
  return _reportFromBackend(data);
}

/** GET /reports/<id> — single doc, for polling status. */
export async function getGbusinessReport(id) {
  const { data } = await httpClient.get(`${BASE}/reports/${encodeURIComponent(id)}`);
  return _reportFromBackend(data);
}

/** @typedef {{locationId: string|null, reportType: string, status: string|null,
 *   readiness: Record<string, boolean>, ready: boolean,
 *   reportId: string|null}} WelcomeReadinessItem */

function _welcomeItemFromBackend(raw) {
  return {
    locationId: raw.location_id ?? null,
    reportType: raw.report_type || 'welcome',
    status: raw.status ?? null,
    readiness: raw.readiness || {},
    ready: !!raw.ready,
    reportId: raw.report_id || null,
  };
}

/**
 * GET /reports/welcome/readiness — readiness del informe de bienvenida de
 * CADA local activo (más la del agregado de marca, con `locationId: null`).
 *
 * Una sola petición alimenta las N tarjetas de la página; el llamante indexa
 * por `locationId`. `aggregatedEligible` dice si esta cuenta llega al umbral de
 * locales que da derecho al informe de MARCA — la pantalla lo necesita para
 * poder configurar sus destinatarios antes de que exista el primer PDF.
 * @returns {Promise<{items: WelcomeReadinessItem[], aggregatedEligible: boolean}>}
 */
export async function getWelcomeReportReadiness() {
  const { data } = await httpClient.get(`${BASE}/reports/welcome/readiness`);
  // Objeto y no array pelado: `aggregatedEligible` colgado como propiedad del
  // array no sobreviviría al structural sharing de react-query, que reconstruye
  // los arrays al comparar y se dejaría por el camino cualquier campo extra.
  return {
    items: (data?.items || []).map(_welcomeItemFromBackend),
    aggregatedEligible: !!data?.aggregated_eligible,
  };
}

/**
 * POST /reports/welcome/generate — generate the welcome report on demand.
 * 202 when generation was enqueued, 409 (thrown by httpClient) when data
 * isn't ready yet. Reuses the standard generation, so the welcome Loops
 * email is sent on success.
 */
export async function generateWelcomeReport(locationId) {
  const { data } = await httpClient.post(`${BASE}/reports/welcome/generate`, {
    location_id: locationId,
  });
  return _welcomeItemFromBackend(data);
}

/**
 * Clave del informe de MARCA en el mapa de destinatarios. No es un comodín: el
 * agregado es un informe concreto, cuyo `location_id` en el backend es null y
 * por tanto no sirve de clave. Ojo, nada que ver con el `'all'` de las reglas de
 * automatizaciones, que ahí sí significa "todos los locales".
 */
export const AGGREGATED_REPORT_KEY = 'aggregated';

/** @typedef {{enabled: boolean, emails: string[]}} ReportEmailNotification */

/**
 * GET /reports/email-notifications — a quién se le manda cada informe, además
 * del titular de la cuenta.
 *
 * Devuelve un mapa `{ [locationId | 'aggregated']: {enabled, emails} }`. Las
 * claves que no estén configuradas simplemente no vienen.
 * @returns {Promise<Record<string, ReportEmailNotification>>}
 */
export async function getReportEmailNotifications() {
  const { data } = await httpClient.get(`${BASE}/reports/email-notifications`);
  const salida = {};
  Object.entries(data || {}).forEach(([clave, valor]) => {
    salida[clave] = {
      enabled: !!valor?.enabled,
      emails: Array.isArray(valor?.emails) ? valor.emails : [],
    };
  });
  return salida;
}

/**
 * PUT /reports/email-notifications — guarda el mapa COMPLETO.
 *
 * Se manda entero, no por local: el backend hace un `$set` del campo, así que
 * enviar solo el local que se acaba de tocar borraría los demás. El llamante
 * debe partir de lo que devolvió el GET y sustituir la clave que cambió.
 *
 * Un 402 aquí significa que ese local no tiene asiento Pro (`seat_required`), y
 * lo entiende `lib/seatRequired.js`.
 */
export async function saveReportEmailNotifications(mapa) {
  const cuerpo = {};
  Object.entries(mapa || {}).forEach(([clave, valor]) => {
    cuerpo[clave] = {
      enabled: !!valor?.enabled,
      emails: Array.isArray(valor?.emails) ? valor.emails : [],
    };
  });
  const { data } = await httpClient.put(`${BASE}/reports/email-notifications`, cuerpo);
  const salida = {};
  Object.entries(data || {}).forEach(([clave, valor]) => {
    salida[clave] = {
      enabled: !!valor?.enabled,
      emails: Array.isArray(valor?.emails) ? valor.emails : [],
    };
  });
  return salida;
}

/** DELETE /reports/<id> — manual delete (rare; TTL handles routine). */
export async function deleteGbusinessReport(id) {
  await httpClient.delete(`${BASE}/reports/${encodeURIComponent(id)}`);
  return true;
}

/**
 * GET /reports/<id>/download — returns either {url, expires_in} (R2 presigned)
 * or a Blob of the PDF bytes (filesystem streaming dev backend). Caller
 * inspects the shape and either window.open(url) or saves the blob.
 */
export async function getGbusinessReportDownload(id) {
  const { data, headers } = await httpClient.get(
    `${BASE}/reports/${encodeURIComponent(id)}/download`,
    { responseType: 'blob' },
  );
  // The blob backend returns application/pdf; the R2 backend returns JSON.
  const ct = (headers['content-type'] || '').toLowerCase();
  if (ct.startsWith('application/json')) {
    // Re-parse the blob as JSON for the presigned-URL path.
    const text = await data.text();
    return JSON.parse(text);
  }
  return data; // Blob
}

// ---------------------------------------------------------------------------
// Scanner SEO (rejilla de ranking en Google Maps, estilo Local Falcon).
//
// A diferencia del resto de la sección, NO es Pro-only: free y Pro comparten
// los 3 escaneos gratis por local. El escaneo corre en background porque son
// 25-49 llamadas a un proveedor externo (~40 s), muy por encima del timeout de
// 15 s de `httpClient`: el POST responde 202 con un `id` y la página sondea.
// ---------------------------------------------------------------------------

/**
 * GET /geogrid/quota?location_id=X
 *
 * Estado de la cuota del local, sin consumir nada. `providerReady` a false
 * significa que el entorno no tiene credenciales de DataForSEO: la UI debe
 * deshabilitar el botón con un motivo, no dejar pulsar para comerse un 502.
 */
export async function getGeogridQuota(locationId) {
  const { data } = await httpClient.get(`${BASE}/geogrid/quota`, {
    params: { location_id: locationId },
  });
  return {
    locationId: data.location_id,
    freeLimit: data.free_limit ?? 0,
    freeUsed: data.free_used ?? 0,
    freeRemaining: data.free_remaining ?? 0,
    credits: data.credits ?? 0,
    canScan: !!data.can_scan,
    providerReady: !!data.provider_ready,
    // Tope de puntos de «Puntos a medida». Lo manda el servidor para no tener
    // aquí una copia que se quede atrás; 200 es su valor por defecto.
    customMaxPoints: data.custom_max_points ?? 200,
    // Administradores de Tapstar: escanean sin gastar cuota, para poder dar
    // soporte sin quemarle al cliente sus escaneos de por vida.
    unlimited: !!data.unlimited,
    // Con qué moneda se le cobra a ESTE usuario. Viajan las dos porque el
    // conmutador es por usuario: mientras esté a medias, un mismo despliegue
    // tiene gente en cada régimen y enseñar una sola cifra sería mentira para
    // la mitad. `creditsActive` es lo que decide cuál se pinta.
    creditsActive: !!data.credits_active,
    creditsMonthly: data.credits_monthly ?? 0,
    creditsPurchased: data.credits_purchased ?? 0,
    creditsPeriodEnd: data.credits_period_end || null,
    // El siguiente hito del funnel: qué gana haciendo la siguiente cosa. El
    // saldo no se enseña nunca solo. `milestone` es la acción que le toca, y de
    // ahí sale la frase — incluido el «por local» de los dos hitos de bolsa, que
    // vive en el texto y no en un campo aparte.
    creditsNext: data.credits_next
      ? {
        milestone: data.credits_next.milestone,
        credits: data.credits_next.credits ?? 0,
      }
      : null,
    // Lo que ha ganado y todavía no ha visto. Lo decide el SERVIDOR y no la URL
    // de vuelta de Stripe: los créditos los concede el webhook `invoice.paid`,
    // que puede llegar después de que el usuario vuelva del checkout, así que un
    // diálogo disparado por `?stripe=success` anunciaría créditos que aún no
    // existen. Y así da igual por qué pantalla entre o desde qué dispositivo.
    creditsCelebrate: data.credits_celebrate
      ? {
        milestone: data.credits_celebrate.milestone,
        credits: data.credits_celebrate.credits ?? 0,
      }
      : null,
  };
}

/**
 * POST /geogrid/credits/celebrated
 *
 * «Ya lo he visto»: cierra el aviso de celebración en el servidor. Sin cuerpo —
 * el servidor marca lo que de verdad hay pendiente, no lo que diga el cliente.
 */
export async function markGeogridCreditsCelebrated() {
  const { data } = await httpClient.post(`${BASE}/geogrid/credits/celebrated`);
  return !!data.celebrated;
}

/**
 * POST /geogrid/keyword-suggestions
 *
 * Sugerencias de keyword por IA a partir de la categoría y la ciudad del local.
 * Es SÍNCRONO (1-3 s): a diferencia de los demás endpoints de IA no encola ni
 * hay que sondearlo, porque el trabajo no da para ello.
 */
export async function getGeogridKeywordSuggestions(locationId) {
  const { data } = await httpClient.post(`${BASE}/geogrid/keyword-suggestions`, {
    location_id: locationId,
  });
  return {
    suggestions: data.suggestions || [],
    cached: !!data.cached,
    lang: data.lang || null,
  };
}

/**
 * POST /geogrid/scans — lanza un escaneo. GASTA una unidad de cuota.
 *
 * Devuelve 202 con el escaneo ya creado (rejilla incluida, con las posiciones
 * a null) para que la página pueda pintar la cuadrícula vacía al instante en
 * vez de dejar al usuario mirando un hueco 40 segundos.
 *
 * Errores que la UI tiene que distinguir, por `code`:
 * - `geogrid_quota_exhausted` (402): agotados los gratis → CTA de créditos.
 * - `geogrid_scan_already_running` (409): trae `scan_id` para engancharse.
 * - `geogrid_location_missing_coordinates` (422): local sin coordenadas.
 * - `geogrid_provider_error` (502): proveedor caído o sin credenciales.
 */
/**
 * El objetivo de escaneo de una cuenta demo (decisiones §139).
 *
 * 404 significa «esta cuenta no es demo», no «hay un problema»: la ruta no
 * existe para nadie más. Por eso se traduce a `null` en vez de propagar — quien
 * llama solo quiere saber si hay engranaje que pintar.
 */
export async function getDemoScanTarget() {
  try {
    const { data } = await httpClient.get(`${BASE}/geogrid/demo-target`);
    return data.target || null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

export async function setDemoScanTarget({ placeId, title, address, latitude, longitude }) {
  const { data } = await httpClient.put(`${BASE}/geogrid/demo-target`, {
    placeId, title, address, latitude, longitude,
  });
  return data.target || null;
}

export async function clearDemoScanTarget() {
  await httpClient.delete(`${BASE}/geogrid/demo-target`);
  return null;
}


export async function createGeogridScan({
  locationId, keyword, gridSize, stepKm, mode = 'grid', points = [],
}) {
  const body = {
    location_id: locationId,
    keyword,
    // Distancia entre puntos. El backend deriva de aquí el radio y el zoom. En
    // «Puntos a medida» no hay distancia entre puntos: es la ESCALA, y solo
    // decide el zoom.
    step_km: stepKm,
  };
  if (mode === 'custom') {
    // Los puntos que marcó el usuario, en el orden del clic. El backend los
    // valida, quita repetidos y cobra uno por punto.
    body.mode = 'custom';
    body.points = points.map((p) => ({ latitude: p.lat, longitude: p.lng }));
  } else {
    body.grid_size = gridSize;
  }
  const { data } = await httpClient.post(`${BASE}/geogrid/scans`, body);
  return normalizeGeogridScan(data);
}

/**
 * El borrador de «Puntos a medida» de un local: `{points: [{lat, lng}], stepKm}`.
 *
 * Se guarda en el servidor mientras se marca (PUT con debounce) y al lanzar,
 * para que repetir el escaneo sobre los mismos sitios no obligue a marcarlos
 * otra vez. `points` vacío si el local no tiene ninguno.
 */
function normalizeGeogridPointSet(data) {
  return {
    points: (data?.points || []).map((p) => ({ lat: p.latitude, lng: p.longitude })),
    stepKm: data?.step_km ?? null,
  };
}

/** GET /geogrid/point-set?location_id=X */
export async function getGeogridPointSet(locationId) {
  const { data } = await httpClient.get(`${BASE}/geogrid/point-set`, {
    params: { location_id: locationId },
  });
  return normalizeGeogridPointSet(data);
}

/** PUT /geogrid/point-set?location_id=X — guarda el borrador (1..máximo puntos). */
export async function saveGeogridPointSet({ locationId, points, stepKm }) {
  const { data } = await httpClient.put(
    `${BASE}/geogrid/point-set`,
    {
      points: points.map((p) => ({ latitude: p.lat, longitude: p.lng })),
      step_km: stepKm,
    },
    { params: { location_id: locationId } },
  );
  return normalizeGeogridPointSet(data);
}

/** DELETE /geogrid/point-set?location_id=X — «Borrar todos». */
export async function deleteGeogridPointSet(locationId) {
  await httpClient.delete(`${BASE}/geogrid/point-set`, {
    params: { location_id: locationId },
  });
}

/**
 * PUT /locations/<id>/coordinates — fija a mano el centro del Mapa SEO.
 *
 * El punto se elige en el NAVEGADOR (autocompletado de Places y un pin
 * arrastrable) y aquí solo se persiste. Geocodificar en servidor no era opción:
 * `GOOGLE_BACKEND_API_KEY` está restringida por IP y solo responde desde
 * producción, así que el flujo sería imposible de probar en dev.
 *
 * `address` es la dirección que usó el USUARIO, no la que Google tiene de la
 * ficha: es la que explica de dónde salió el punto, y en un negocio de área de
 * servicio pueden no tener nada que ver.
 *
 * Errores que la UI tiene que distinguir, por `code`:
 * - `invalid_coordinates` (400): fuera de rango, no finita o Isla Nula.
 * - `geogrid_location_not_found` (404): el local no es de este inquilino.
 * - `seat_required` (402): el local no lleva asiento — lo trata `useSeatRequired`.
 */
export async function setLocationCoordinates({
  locationId, latitude, longitude, address, placeId,
}) {
  const { data } = await httpClient.put(
    `${BASE}/locations/${encodeURIComponent(locationId)}/coordinates`,
    { latitude, longitude, address, place_id: placeId || '' },
  );
  return {
    locationId: data.location_id,
    latitude: data.latitude,
    longitude: data.longitude,
    coordsSource: data.coords_source,
    coordsAddress: data.coords_address || '',
    coordsPlaceId: data.coords_place_id || '',
  };
}

/** GET /geogrid/scans?location_id=X&page=N — histórico paginado y ligero. */
export async function listGeogridScans({ locationId, page = 1 } = {}) {
  const { data } = await httpClient.get(`${BASE}/geogrid/scans`, {
    params: { location_id: locationId, page },
  });
  return {
    count: data.count || 0,
    page: data.page || 1,
    nextPage: data.next_page || null,
    previousPage: data.previous_page || null,
    results: (data.results || []).map((row) => ({
      id: row.id,
      keyword: row.keyword,
      status: row.status,
      gridSize: row.grid_size,
      // `grid` o `custom` («Puntos a medida»); los escaneos viejos no lo traen.
      mode: row.mode || 'grid',
      pointsCount: row.points_count ?? null,
      metrics: row.metrics || {},
      createdAt: row.created_at,
      finishedAt: row.finished_at || null,
      // Lanzado por soporte en modo admin (§172). Solo viene a true a un admin:
      // al cliente ni siquiera le llegan esas filas.
      launchedBySupport: row.launched_by_support === true,
    })),
  };
}

/**
 * GET /geogrid/scans/summary — qué locales tienen ya escaneos terminados.
 * Solo vienen los que tienen alguno: `{ [locationId]: { count, lastScanAt } }`.
 */
export async function getGeogridScanSummary() {
  const { data } = await httpClient.get(`${BASE}/geogrid/scans/summary`);
  return Object.fromEntries(
    Object.entries(data?.locations || {}).map(([id, row]) => [id, {
      count: row.count || 0,
      lastScanAt: row.last_scan_at || null,
    }]),
  );
}

/**
 * GET /geogrid/scans/<id>
 *
 * @param {object}  opts
 * @param {boolean} opts.progressOnly  recorta el top-20 de cada punto, que es
 *   el 95 % del peso y que la UI no mira hasta que el escaneo termina. Úsalo
 *   MIENTRAS sondeas: sin él, un escaneo de 40 s son ~20 sondeos arrastrando un
 *   documento que crece hasta 40 KB.
 * @param {string}  opts.competitorPlaceId  añade la comparación 1-a-1. No
 *   cuesta nada: la posición del competidor en cada punto ya está guardada, así
 *   que cambiar de competidor no dispara ninguna petición al proveedor.
 */
export async function getGeogridScan(scanId, { progressOnly = false, competitorPlaceId } = {}) {
  const params = {};
  if (progressOnly) params.view = 'progress';
  if (competitorPlaceId) params.competitor_place_id = competitorPlaceId;

  const { data } = await httpClient.get(
    `${BASE}/geogrid/scans/${encodeURIComponent(scanId)}`, { params },
  );
  return normalizeGeogridScan(data, { partial: progressOnly });
}

/**
 * Qué se hizo con la costa: `{sea, relocated, dropped}`, o null cuando no se
 * miró (conmutador apagado, o un escaneo anterior a esto). `sea` son los puntos
 * del cuadrado que caían en el mar; `relocated`, los que se movieron tierra
 * adentro; `dropped`, los que no cupieron y no se miden (ni se cobran).
 */
export function normalizeGeogridLayout(layout) {
  if (!layout) return null;
  return {
    sea: layout.sea || 0,
    relocated: layout.relocated || 0,
    dropped: layout.dropped || 0,
  };
}

/**
 * GET /geogrid/preview — los puntos que se van a medir, antes de lanzar.
 *
 * No cobra nada. Existe por la costa: con puntos en el mar la rejilla deja de
 * ser el cuadrado que `buildPreviewGrid` sabe dibujar solo, y lo que se cobra
 * es el número de puntos (`pointsCount`), que puede ser menor que N².
 */
export async function getGeogridPreview({ locationId, gridSize, stepKm }) {
  const { data } = await httpClient.get(`${BASE}/geogrid/preview`, {
    params: { location_id: locationId, grid_size: gridSize, step_km: stepKm },
  });
  return {
    points: (data.points || []).map((p) => ({
      i: p.i,
      j: p.j,
      lat: p.latitude,
      lng: p.longitude,
      relocated: !!p.relocated,
    })),
    pointsCount: data.points_count ?? (data.points || []).length,
    layout: normalizeGeogridLayout(data.layout),
  };
}

/**
 * snake_case → camelCase de un escaneo. Compartido por las tres funciones.
 *
 * `partial` marca que la respuesta vino con la proyección ligera y que, por
 * tanto, NO trae `businesses` ni `competitors`. Quien poletea lo usa para saber
 * que aún le falta una lectura completa: el sondeo que ve por primera vez el
 * estado `done` se pidió cuando el escaneo todavía corría, así que llega
 * recortado, y sin esta marca la tabla de competidores se quedaría vacía para
 * siempre en cuanto el sondeo se detuviera.
 */
function normalizeGeogridScan(data, { partial = false } = {}) {
  const params = data.params || {};
  const progress = data.progress || {};
  return {
    id: data.id,
    locationId: data.location_id,
    keyword: data.keyword,
    status: data.status,
    // `matchedBy: 'title'` significa que no teníamos el Place ID del local y el
    // match se hizo por nombre + cercanía. La UI lo avisa: es fiable, pero
    // menos que una coincidencia exacta de identificador.
    matchedBy: data.matched_by || null,
    placeId: data.place_id || null,
    ownTitle: data.own_title || '',
    params: {
      // `custom` = «Puntos a medida»: `gridSize` es null y los puntos llevan
      // una identidad sintética (i = orden del clic, j = 0).
      mode: params.mode || 'grid',
      pointsCount: params.points_count ?? null,
      gridSize: params.grid_size,
      // `stepKm` es lo que eligió el usuario; `radiusKm` es la consecuencia.
      stepKm: params.step_km,
      radiusKm: params.radius_km,
      zoom: params.zoom,
      depth: params.depth,
    },
    center: data.center || null,
    progress: {
      total: progress.total || 0,
      done: progress.done || 0,
      failed: progress.failed || 0,
    },
    errorCode: data.error_code || null,
    partial,
    points: (data.points || []).map((p) => ({
      i: p.i,
      j: p.j,
      lat: p.latitude,
      lng: p.longitude,
      rank: p.rank,
      // `?? true` para los escaneos guardados antes de que existiera el campo:
      // están terminados, así que todos sus puntos están medidos. Sin esto, un
      // escaneo del histórico se pintaría entero translúcido.
      measured: p.measured ?? true,
      // Se consultó y DataForSEO no llegó a responder: "sin dato", que NO es
      // "no apareces" aunque los dos lleguen con `rank: null`.
      failed: !!p.failed,
      // Top-N de ese punto. Ausente en las respuestas del sondeo, que lo
      // excluyen por peso: el diálogo de ranking solo se abre con el escaneo ya
      // terminado, cuando la lectura completa sí lo trae.
      results: p.results || [],
      // El punto caía en el mar y se movió tierra adentro, a un nodo de la
      // misma malla fuera del cuadrado: sus `i`/`j` pueden salirse de 0..N-1.
      relocated: !!p.relocated,
      // El punto del propio negocio en un escaneo a medida: lo pone el
      // servidor, no el usuario (siempre el 0).
      anchor: !!p.anchor,
    })),
    layout: normalizeGeogridLayout(data.layout),
    businesses: data.businesses || {},
    metrics: data.metrics || {},
    pattern: data.pattern || {},
    competitors: (data.competitors || []).map((c) => ({
      placeId: c.place_id,
      appearances: c.appearances,
      avgRank: c.avg_rank,
      bestRank: c.best_rank,
      solvPct: c.solv_pct,
      sharedPoints: c.shared_points,
      beatsUsPct: c.beats_us_pct,
      // Marca a un HERMANO de la misma franquicia, no un rival de verdad. Solo
      // sale cuando el backend detecta solape (ver `group` más abajo).
      isOwnGroup: !!c.is_own_group,
    })),
    comparison: data.comparison
      ? {
        placeId: data.comparison.place_id,
        points: data.comparison.points || [],
        summary: data.comparison.summary || { wins: 0, losses: 0, ties: 0 },
      }
      : null,
    // Dominancia de GRUPO: otro local del mismo dueño (una franquicia con
    // varias sucursales en la misma zona) detectado dentro de ESTA rejilla, sin
    // ningún escaneo adicional. Ausente cuando no hay solape — ver
    // `GeoGridScanDetailView` en el backend.
    group: data.group
      ? {
        metrics: data.group.metrics || {},
        // La mejor posición del GRUPO en cada punto, y de QUIÉN es
        // (`placeId` a null = el mejor somos nosotros). Es lo que el mapa
        // necesita para poder pintar "esta esquina la cubre tu otro local".
        points: (data.group.points || []).map((p) => ({
          i: p.i,
          j: p.j,
          rank: p.rank,
          placeId: p.place_id || null,
        })),
        members: (data.group.members || []).map((m) => ({
          locationId: m.location_id,
          title: m.title,
          placeId: m.place_id,
          appearances: m.appearances,
          avgRank: m.avg_rank,
          bestRank: m.best_rank,
          solvPct: m.solv_pct,
        })),
      }
      : null,
    ai: data.ai || null,
    aiError: data.ai_error || null,
    costUsd: data.cost_usd || 0,
    createdAt: data.created_at,
    finishedAt: data.finished_at || null,
  };
}


/**
 * Apunta que el inquilino ha abierto una funcion del producto.
 *
 * Telemetria para la cifra de "funciones exploradas" de la ficha de admin. El
 * backend responde 204 pase lo que pase (y se traga en silencio la escritura si
 * hay una impersonacion en curso), asi que aqui no hay nada que interpretar:
 * quien llama solo tiene que evitar que un fallo de red rompa la navegacion.
 *
 * @param {string} key clave del catalogo (`src/lib/featureCatalog.js`)
 */
export async function markFeatureSeen(key) {
  await httpClient.post(`${BASE}/feature-seen`, { key });
}
